import React, { useState, useEffect, useRef, Suspense, lazy, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Progress } from "./components/ui/progress";
import { AlertCircle, Brain, FileText, User, Zap, Upload, Search, BookOpen, Download, CheckCircle, XCircle, Clock, MessageCircle, Stethoscope, FlaskConical, Activity, Shield, Users, Network, Award, TrendingUp, Eye, Lock, Star, AlertTriangle, Filter, Menu, X, Calculator, Pill, Layers, Settings, Printer, ChevronDown, ChevronUp, Bookmark, BookmarkPlus } from "lucide-react";
import { getBookmarks, toggleBookmark as toggleBookmarkStore, isBookmarked as isBookmarkedStore, syncBookmarksFromServer } from "./lib/bookmarks";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { format, parseISO, subDays, startOfWeek, endOfWeek } from 'date-fns';
import { Alert, AlertDescription } from "./components/ui/alert";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ProgressTracking from "./components/ProgressTracking";
import AuthGate from "./components/AuthGate";
import ProfilePage from "./components/ProfilePage";
import BillingPage from "./components/BillingPage";
import DosageCalculator from "./components/DosageCalculator";
import KnowledgeAdmin from "./components/KnowledgeAdmin";
import AdminDashboard from "./components/AdminDashboard";
import LabInterpretation from "./components/LabInterpretation";
import ProtocolFeedback from "./components/ProtocolFeedback";
import PatientDashboard from "./components/PatientDashboard";
import DrPeptideChatComponent from "./components/DrPeptideChat";
import AssessmentWizardComponent from "./components/AssessmentWizard";
import ProtocolViewComponent from "./components/ProtocolView";
import { PublicHomePage, AboutDrPeptidePage, PeptideProtocolsPage } from "./components/PublicPages";
import ProviderDirectory from "./components/ProviderDirectory";
import { TermsOfServicePage, PrivacyPolicyPage, AmbassadorTermsPage } from "./components/LegalPages";
import CommunityForum from "./components/CommunityForum";
import NotificationBell from "./components/NotificationBell";
import NotificationsPage from "./components/NotificationsPage";
import { isAuthenticated, getAuthHeaders } from "./lib/authHeaders";
import ProtocolSeoPage from "./components/ProtocolSeoPage";
import PricingComparisonMatrix from "./components/PricingComparisonMatrix";

// Use environment variable for backend URL
const BACKEND_URL = "";
const API = `${BACKEND_URL}/api`;

// Debug logging to verify URL configuration

const PeptideProtocolsApp = ({ currentUser, setCurrentUser, initialView }) => {
  const [currentView, setCurrentView] = useState(initialView || 'home');
  // Beta access check — used to gate subscription-only views
  const _isBetaOnly = (() => { try { const b = JSON.parse(localStorage.getItem("beta_access") || "{}"); return b.granted && b.code === "1111" && !currentUser?.email; } catch { return false; } })();
  // Post id to deep-link to when opening Community from a notification.
  const [communityPostId, setCommunityPostId] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [assessmentId, setAssessmentId] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [assessment, setAssessment] = useState({
    patient_name: '',
    age: '',
    gender: '',
    weight: '',
    height_feet: '',
    height_inches: '',
    email: '',
    primary_concerns: [],
    health_goals: [],
    current_medications: [],
    lifestyle_factors: {},
    medical_history: [],
    allergies: []
  });
  const [generatedProtocol, setGeneratedProtocol] = useState(null);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [showWelcome, setShowWelcome] = useState(() => {
    return localStorage.getItem('pp_show_welcome') === 'true';
  });
  const [loading, setLoading] = useState(false);
  const [protocolQuestions, setProtocolQuestions] = useState([]);
  const [protocolQuestionLoading, setProtocolQuestionLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [extractionPreview, setExtractionPreview] = useState(null);
  const [showExtractionModal, setShowExtractionModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [patientSummary, setPatientSummary] = useState(null);
  const [showPatientSummary, setShowPatientSummary] = useState(false);
  const [generatingPatientSummary, setGeneratingPatientSummary] = useState(false);
  const [protocolLibrary, setProtocolLibrary] = useState([]);
  const [peptidesDatabase, setPeptidesDatabase] = useState([]);
  const [peptideCategories, setPeptideCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPeptide, setSelectedPeptide] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [patientContext, setPatientContext] = useState('');
  const [uploadedLabFiles, setUploadedLabFiles] = useState([]);
  const [showPatientInfo, setShowPatientInfo] = useState(true);
  const [accessCode, setAccessCode] = useState('');
  const [protocolProgress, setProtocolProgress] = useState(null);
  const chatInputRef = useRef(null);
  const fullNameRef = useRef(null);
  const weightRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);
  const patientContextRef = useRef(null);
  const protocolQuestionInputRef = useRef(null);
  
  // Additional refs for uncontrolled inputs to fix cursor issues
  const ageRef = useRef(null);
  const emailRef = useRef(null);

  // Feedback System State
  const [showFeedbackChat, setShowFeedbackChat] = useState(false);
  const [showProtocolFeedback, setShowProtocolFeedback] = useState(false);
  const [showErrorReport, setShowErrorReport] = useState(false);
  const [feedbackChatResponse, setFeedbackChatResponse] = useState('');
  const [loadingFeedbackChat, setLoadingFeedbackChat] = useState(false);
  const [protocolRating, setProtocolRating] = useState(0);
  const [improvementsNoticed, setImprovementsNoticed] = useState([]);
  const [protocolFeedbackResponse, setProtocolFeedbackResponse] = useState('');
  const [loadingProtocolFeedback, setLoadingProtocolFeedback] = useState(false);
  const [errorReportResponse, setErrorReportResponse] = useState('');
  const [loadingErrorReport, setLoadingErrorReport] = useState(false);
  
  // Lab Interpretation State
  const [labAnalysisData, setLabAnalysisData] = useState(null);
  const [showLabInterpretation, setShowLabInterpretation] = useState(false);
  
  // Feedback refs
  const feedbackChatInputRef = useRef(null);
  const sideEffectsRef = useRef(null);
  const additionalFeedbackRef = useRef(null);
  const errorTypeRef = useRef(null);
  const incorrectInfoRef = useRef(null);
  const correctInfoRef = useRef(null);
  const severityRef = useRef(null);

  // A3: Global error handlers for debugging
  useEffect(() => {
    const origOnerror = window.onerror;
    const origUnhandled = window.onunhandledrejection;
    window.onerror = (msg, source, lineno, colno, error) => {
      console.error('[PeptideProtocols Global Error]', { msg, source, lineno, colno, error });
      if (origOnerror) origOnerror(msg, source, lineno, colno, error);
    };
    window.onunhandledrejection = (event) => {
      console.error('[PeptideProtocols Unhandled Promise]', event.reason);
      if (origUnhandled) origUnhandled(event);
    };
    return () => { window.onerror = origOnerror; window.onunhandledrejection = origUnhandled; };
  }, []);

  // Auto-save functionality
  const saveToLocalStorage = (assessmentData, step, assessmentIdLocal) => {
    try {
      const saveData = {
        assessment: assessmentData,
        currentStep: step,
        assessmentId: assessmentIdLocal,
        timestamp: new Date().toISOString(),
        lastSaved: Date.now()
      };
      localStorage.setItem('peptideAssessmentDraft', JSON.stringify(saveData));
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
    }
  };

  const loadFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem('peptideAssessmentDraft');
      if (saved) {
        const saveData = JSON.parse(saved);
        // Only load if saved within last 7 days
        if (Date.now() - saveData.lastSaved < 7 * 24 * 60 * 60 * 1000) {
          return saveData;
        } else {
          // Remove expired save
          localStorage.removeItem('peptideAssessmentDraft');
        }
      }
    } catch (error) {
      console.error('Failed to load from localStorage:', error);
      localStorage.removeItem('peptideAssessmentDraft'); // Remove corrupted data
    }
    return null;
  };

  const clearAutoSave = () => {
    try {
      localStorage.removeItem('peptideAssessmentDraft');
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }
  };

  // Deploy 4-A: explicit "New Assessment" starts a genuinely fresh patient assessment.
  // Resets all patient-specific state + clears the saved draft so no prior answers/ID/protocol carry over.
  // Does NOT alter the saved DB assessment/protocol; next save creates a new assessmentId.
  const startNewAssessment = () => {
    // Cancel any pending debounced autosave so a stale timer can't re-persist prior answers after reset.
    if (autoSaveTimeoutRef.current) { clearTimeout(autoSaveTimeoutRef.current); }
    clearAutoSave();
    setAssessment({
      patient_name: '',
      age: '',
      gender: '',
      weight: '',
      height_feet: '',
      height_inches: '',
      email: '',
      primary_concerns: [],
      health_goals: [],
      current_medications: [],
      lifestyle_factors: {},
      medical_history: [],
      allergies: []
    });
    setAssessmentId(null);
    setGeneratedProtocol(null);
    setProtocolProgress(null);
    setLoading(false);
    setCurrentStep(1);
    setCurrentView('assessment');
  };

  const autoSave = async (assessmentData, step, assessmentIdLocal) => {
    // Always save to localStorage for instant recovery
    saveToLocalStorage(assessmentData, step, assessmentIdLocal);
    
    // Also try to save to server if we have an assessment ID
    if (assessmentIdLocal && !isAutoSaving) {
      setIsAutoSaving(true);
      try {
        await saveAssessmentStep(step, assessmentData);
      } catch (error) {
        console.warn('Server auto-save failed, localStorage backup available:', error);
      } finally {
        setIsAutoSaving(false);
      }
    }
  };

  // Debounced auto-save function to prevent re-renders
  const debouncedAutoSave = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    autoSaveTimeoutRef.current = setTimeout(() => {
      // Get current form values from refs instead of state
      const formData = {
        patient_name: fullNameRef.current?.value || '',
        age: ageRef.current?.value || '',
        weight: weightRef.current?.value || '',
        email: emailRef.current?.value || '',
        ...assessment // Keep other assessment data
      };
      if (formData && currentStep && assessmentId) {
        autoSave(formData, currentStep, assessmentId);
      }
    }, 2000);
  }, [currentStep, assessmentId, assessment]);

  const handleAssessmentChange = useCallback((field, value) => {
    // Only update state, don't trigger auto-save here to prevent re-renders during typing
    setAssessment(prev => ({
      ...prev,
      [field]: value
    }));
    // Trigger debounced auto-save
    debouncedAutoSave();
  }, [debouncedAutoSave]);

  const handleLifestyleFactorChange = (factor, value) => {
    const updatedAssessment = {
      ...assessment,
      lifestyle_factors: {
        ...assessment.lifestyle_factors,
        [factor]: value
      }
    };
    setAssessment(updatedAssessment);
  };

  const addToListField = (field, value) => {
    if (value.trim()) {
      setAssessment(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
    }
  };

  const removeFromListField = (field, index) => {
    setAssessment(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const saveAssessmentStep = async (step, data) => {
    try {
      setLoading(true);
      
      const response = await axios.post(`${API}/assessment/multi-step${assessmentId ? `?assessment_id=${assessmentId}` : ''}`, {
        step,
        assessment_data: data
      }, { headers: getAuthHeaders() });
      
      
      if (!assessmentId) {
        setAssessmentId(response.data.id);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error saving assessment step:', error);
      console.error('Error details:', error.response?.data);
      alert('Error saving assessment. Please try again.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files, fileType = 'labs') => {
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('files', file);
    });
    formData.append('file_type', fileType); // Add type indicator

    try {
      setUploadingFiles(true);
      
      // Show which files are being processed
      const fileNames = Array.from(files).map(f => f.name).join(', ');
      const typeLabel = fileType === 'genetics' ? '🧬 genetic test' : '🔬 lab';
      
      let uploadUrl = `${API}/${fileType}/analyze`; // Different endpoint for genetics
      
      try {
        // Attempt backend analysis
        const response = await axios.post(uploadUrl, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000
        });
        
        if (response.data && response.data.analyses) {
          
          // Show extraction preview modal
          setExtractionPreview({
            files: response.data.analyses,
            rawData: response.data,
            fileType: fileType
          });
          setShowExtractionModal(true);
          setUploadedFiles(prev => [...prev, ...response.data.analyses.map(a => ({...a, fileType}))]);
        }
      } catch (apiError) {
        console.warn(`⚠️ Backend ${typeLabel} analysis unavailable:`, apiError.message);
        
        // Fallback: Create preview with file metadata
        const fallbackAnalyses = Array.from(files).map(file => ({
          filename: file.name,
          filesize: file.size,
          filetype: file.type,
          fileType: fileType,
          analysis: {
            extracted_text: fileType === 'genetics' 
              ? `Genetic Test: ${file.name} (${(file.size / 1024).toFixed(2)} KB)\n\n🧬 GENETIC ANALYSIS:\nBackend analysis unavailable. This file will be processed for:\n- Risk variant identification\n- Metabolic gene markers\n- Preventive peptide recommendations\n\nPlease enter known genetic markers manually or wait for backend processing.`
              : `Lab Results: ${file.name} (${(file.size / 1024).toFixed(2)} KB)\n\n🔬 LAB ANALYSIS:\nBackend analysis unavailable. Please enter lab values manually.`,
            status: 'pending_manual_entry'
          },
          raw_file: file
        }));
        
        setExtractionPreview({
          files: fallbackAnalyses,
          fallback: true,
          fileType: fileType
        });
        setShowExtractionModal(true);
        setUploadedFiles(prev => [...prev, ...fallbackAnalyses]);
      }
    } catch (error) {
      console.error(`❌ ${fileType} file upload error:`, error);
      alert(`❌ Error processing ${fileType} files. Please try again or enter values manually.`);
    } finally {
      setUploadingFiles(false);
    }
  };
  
  const removeUploadedFile = (filename) => {
    setUploadedFiles(prev => prev.filter(f => f.filename !== filename));
  };
  
  const generatePatientSummary = async (clinicalResponse) => {
    setGeneratingPatientSummary(true);
    
    try {
      const summaryPrompt = `You are a medical communication expert. Convert this clinical practitioner response into a clear, patient-friendly summary that a doctor can print and hand to their patient.

CLINICAL RESPONSE:
${clinicalResponse}

Create a PATIENT-FRIENDLY SUMMARY that:
- Uses simple, everyday language (avoid medical jargon)
- Explains medical terms when they must be used
- Focuses on actionable information the patient needs
- Maintains accuracy but increases accessibility
- Includes clear next steps
- Is encouraging and supportive in tone
- Can be printed on 1-2 pages

Format with clear headings and bullet points for easy reading.`;

      const response = await axios.post(`${API}/dr-peptide/chat`, {
        message: summaryPrompt,
        conversation_history: []
      }, {
        timeout: 60000
      });

      if (response.data.success) {
        setPatientSummary({
          original: clinicalResponse,
          summary: response.data.response,
          timestamp: new Date().toLocaleString()
        });
        setShowPatientSummary(true);
      }
    } catch (error) {
      console.error('Error generating patient summary:', error);
      alert('❌ Error generating patient summary. Please try again.');
    } finally {
      setGeneratingPatientSummary(false);
    }
  };

  const applyExtractedData = () => {
    if (!extractionPreview) return;
    
    const analysisText = extractionPreview.files.map(file => {
      const analysis = file.analysis || {};
      const text = analysis.extracted_text || analysis.interpretation || JSON.stringify(analysis, null, 2);
      return `\n\n📋 ${file.filename}:\n${text}`;
    }).join('\n');
    
    // Add with highlighting marker
    const timestamp = new Date().toLocaleTimeString();
    setPatientContext(prev => (prev || '') + `\n\n✨ AUTO-ADDED ${timestamp} ✨${analysisText}\n`);
    
    setShowExtractionModal(false);
    setExtractionPreview(null);
  };

  const generateFunctionalProtocol = async () => {
    if (!assessmentId) {
      alert('Please complete the assessment first');
      return;
    }

    setLoading(true);
    setProtocolProgress("Starting protocol generation...");

    try {
      // Step 1 — kick off the background job (returns instantly)
      const startRes = await axios.post(`${API}/generate-functional-protocol/${assessmentId}`, {}, { headers: getAuthHeaders(), timeout: 30000 });
      const jobId = startRes.data?.job_id;
      if (!jobId) {
        // Legacy fallback: if backend returned the protocol directly
        if (startRes.data?.protocol) {
          setGeneratedProtocol(startRes.data.protocol);
          setCurrentView('protocol');
          setProtocolProgress(null);
          return;
        }
        throw new Error('No job ID returned from server');
      }

      // Step 2 — poll until completed or failed (up to 3 minutes)
      const progressMessages = [
        "Analyzing patient health assessment...",
        "Selecting optimal peptide protocols...",
        "Calculating personalized dosing for patient...",
        "Generating clinical recommendations...",
        "Cross-validating with latest research...",
        "Finalizing patient's comprehensive protocol..."
      ];
      let msgIdx = 0;
      const maxAttempts = 150; // 150 x 2s = 300s (5 min)
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 2000));
        if (i % 3 === 0) { setProtocolProgress(progressMessages[msgIdx % progressMessages.length]); msgIdx++; }

        let job = null;
        try {
          const pollRes = await axios.get(`${API}/protocol-job/${jobId}`, { headers: getAuthHeaders(), timeout: 10000 });
          job = pollRes.data;
        } catch (pollErr) {
          if (pollErr.response?.status === 404) throw pollErr;
          continue;
        }
        if (job?.status === 'completed' && job?.protocol) {
          setGeneratedProtocol(job.protocol);
          setCurrentView('protocol');
          setProtocolProgress(null);
          return;
        }
        if (job?.status === 'failed') {
          throw new Error(job.error || 'Protocol generation failed on the server');
        }
      }
      throw new Error('Protocol generation timed out after 5 minutes. Please try again.');
    } catch (error) {
      setProtocolProgress(null);
      alert(`Error generating protocol: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const chatWithDrPeptide = async (message, retryCount = 0) => {
    if (!message || !message.trim()) {
      console.warn('Empty message, skipping');
      return;
    }
    
    // v2.0.1 - GPT-5 Integration - Updated 2025-10-15
    setChatLoading(true);
    
    // Add user message to chat (only on first attempt)
    if (retryCount === 0) {
      const userMessage = { role: 'user', content: message };
      setChatMessages(prev => [...prev, userMessage]);
    }

    try {
      // Enhanced message with patient context and lab files
      let enhancedMessage = message;
      
      // Add patient context from Patient Information box
      if (patientContext && patientContext.trim()) {
        enhancedMessage = `PATIENT INFORMATION:\n${patientContext.trim()}\n\nQUESTION: ${message}`;
      }
      
      // Add uploaded lab file context if available
      if (uploadedFiles.length > 0) {
        const labContext = uploadedFiles.map(file => {
          const analysis = file.analysis || file;
          return `LAB FILE: ${file.filename || 'uploaded file'}\nANALYSIS: ${JSON.stringify(analysis).substring(0, 500)}`;
        }).join('\n\n');
        
        enhancedMessage += `\n\n📋 UPLOADED LAB FILES:\n${labContext}`;
      }
      
      // Enhanced robust API call with longer timeout for GPT-5
      // Use actual conversation history for memory
      const conversationHistory = chatMessages.slice(-6).map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      
      const response = await axios.post(`${API}/dr-peptide/chat`, {
        message: enhancedMessage,
        conversation_history: conversationHistory
      }, {
        timeout: 120000, // 2 minute timeout for GPT-5 responses
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.success) {
        const aiMessage = { role: 'assistant', content: response.data.response };
        setChatMessages(prev => [...prev, aiMessage]);
      } else {
        // Log the actual error for debugging
        console.error('Dr. Peptide API returned error:', response.data);
        const errorMessage = { role: 'assistant', content: response.data?.response || 'I encountered an issue processing your request. Please try again.' };
        setChatMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      // COMPREHENSIVE ERROR LOGGING for debugging
      console.error('🚨 Dr. Peptide Error - Full Details:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          timeout: error.config?.timeout
        }
      });
      
      // Enhanced retry logic for network issues - 5 attempts for reliability
      if (retryCount < 4 && (
        error.code === 'ECONNABORTED' || 
        error.code === 'NETWORK_ERROR' ||
        error.code === 'ERR_NETWORK' ||
        error.response?.status === 502 ||
        error.response?.status === 503 ||
        error.response?.status === 504
      )) {
        const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Exponential backoff
        setTimeout(() => chatWithDrPeptide(message, retryCount + 1), retryDelay);
        return;
      }
      
      // Improved error handling with specific messages
      console.error('❌ Dr. Peptide Final Error:', error.message, error.code, error.response?.status);
      
      let errorMessage = 'I apologize, but I\'m experiencing technical difficulties right now. Please try again.';
      
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Response timeout - the request took too long. Please try a shorter question or try again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Dr. Peptide service endpoint not found. Please contact support.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error occurred. Our team has been notified. Please try again in a moment.';
      } else if (error.response?.status >= 400 && error.response?.status < 500) {
        errorMessage = 'Request error. Please check your input and try again.';
      } else if (error.code === 'ERR_NETWORK' || !error.response) {
        errorMessage = 'Network connection issue. Please check your internet connection and try again.';
      }
      
      const finalErrorMessage = { role: 'assistant', content: errorMessage };
      setChatMessages(prev => [...prev, finalErrorMessage]);
    } finally {
      setChatLoading(false);
      if (retryCount === 0) {
        setChatInput('');
      }
    }
  };

  const loadEnhancedProtocolLibrary = async () => {
    try {
      const response = await axios.get(`${API}/enhanced-library`);
      setProtocolLibrary(response.data);
    } catch (error) {
      console.error('Error loading enhanced library:', error);
      // Fallback to basic library
      try {
        const basicResponse = await axios.get(`${API}/library`);
        setProtocolLibrary(basicResponse.data);
      } catch (fallbackError) {
        console.error('Error loading basic library:', error);
      }
    }
  };

  const loadPeptidesDatabase = async () => {
    try {
      const [peptidesResponse, categoriesResponse] = await Promise.all([
        axios.get(`${API}/peptides`),
        axios.get(`${API}/peptides/categories`)
      ]);
      setPeptidesDatabase(peptidesResponse.data);
      setPeptideCategories(categoriesResponse.data.categories);
    } catch (error) {
      console.error('❌ Error loading peptides database:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      alert(`Failed to load peptides: ${error.message}`);
    }
  };

  useEffect(() => {
    if (currentView === 'peptides' || currentView === 'calculator') {
      loadPeptidesDatabase();
    }
  }, [currentView]);

  // Auto-save restore effect
  useEffect(() => {
    if (currentView === 'assessment') {
      const saved = loadFromLocalStorage();
      if (saved && !assessmentId) {
        // Auto-restore without confirmation for better UX
        setAssessment(saved.assessment);
        setCurrentStep(saved.currentStep);
        setAssessmentId(saved.assessmentId);
      }
    }
  }, [currentView, assessmentId]);

  // Feedback System Functions
  const sendFeedbackChat = async () => {
    const message = feedbackChatInputRef.current?.value;
    if (!message || !message.trim()) return;

    try {
      setLoadingFeedbackChat(true);
      const response = await axios.post(`${API}/dr-peptide/feedback-chat`, {
        message: message.trim(),
        protocol_id: "sample_protocol", // Would be actual protocol ID
        feedback_context: {}
      });

      if (response.data.success) {
        setFeedbackChatResponse(response.data.response);
        if (feedbackChatInputRef.current) {
          feedbackChatInputRef.current.value = '';
        }
      }
    } catch (error) {
      console.error('Error sending feedback chat:', error);
      setFeedbackChatResponse('Sorry, there was an error processing your message. Please try again.');
    } finally {
      setLoadingFeedbackChat(false);
    }
  };

  const submitProtocolFeedback = async () => {
    try {
      setLoadingProtocolFeedback(true);
      
      const feedbackData = {
        protocol_id: "sample_protocol", // Would be actual protocol ID
        feedback_data: {
          protocol_effectiveness: protocolRating,
          specific_outcomes: {
            improvements_noticed: improvementsNoticed,
            side_effects: sideEffectsRef.current?.value || ""
          },
          days_since_start: 30, // Would be calculated
          additional_feedback: additionalFeedbackRef.current?.value || ""
        }
      };

      const response = await axios.post(`${API}/feedback/protocol`, feedbackData);
      
      if (response.data.success) {
        setProtocolFeedbackResponse(response.data.dr_peptide_response);
      }
    } catch (error) {
      console.error('Error submitting protocol feedback:', error);
      setProtocolFeedbackResponse('Thank you for your feedback! We encountered a technical issue but your feedback has been recorded.');
    } finally {
      setLoadingProtocolFeedback(false);
    }
  };

  const submitErrorReport = async () => {
    try {
      setLoadingErrorReport(true);
      
      const reportData = {
        incorrect_content: incorrectInfoRef.current?.value || "",
        corrected_content: correctInfoRef.current?.value || "",
        reporter_type: "user",
        severity: severityRef.current?.value || "medium",
        context: errorTypeRef.current?.value || ""
      };

      const response = await axios.post(`${API}/feedback/error-correction`, reportData);
      
      if (response.data.success) {
        setErrorReportResponse(response.data.dr_peptide_response);
      }
    } catch (error) {
      console.error('Error submitting error report:', error);
      setErrorReportResponse('Thank you for reporting this issue. We have received your report and will review it promptly.');
    } finally {
      setLoadingErrorReport(false);
    }
  };

  const ListFieldInput = ({ field, placeholder, label }) => {
    const [inputValue, setInputValue] = useState('');
    
    return (
      <div className="space-y-2">
        <Label className="text-sm font-medium">{label}</Label>
        <div className="flex gap-2">
          <Input
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addToListField(field, inputValue);
                setInputValue('');
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              addToListField(field, inputValue);
              setInputValue('');
            }}
          >
            Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-1">
          {assessment[field].map((item, index) => (
            <Badge
              key={index}
              variant="secondary"
              className="cursor-pointer"
              onClick={() => removeFromListField(field, index)}
            >
              {item} ✕
            </Badge>
          ))}
        </div>
      </div>
    );
  };

  const [contributions, setContributions] = useState(null);

  useEffect(() => {
    if (currentUser?.email) {
      axios.get(`${API}/practitioner/contributions?email=${encodeURIComponent(currentUser.email)}`)
        .then(r => { if (r.data?.success) setContributions(r.data.contributions); })
        .catch(() => {});
    }
  }, [currentUser?.email]);

  const BADGE_STYLES = {
    gold: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', bar: 'bg-amber-500', icon: 'text-amber-500' },
    silver: { bg: 'bg-slate-50', border: 'border-slate-300', text: 'text-slate-600', bar: 'bg-slate-500', icon: 'text-slate-500' },
    bronze: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700', bar: 'bg-orange-500', icon: 'text-orange-500' },
    starter: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', bar: 'bg-teal-500', icon: 'text-teal-500' },
  };

  const FoundingPractitionerCard = () => {
    if (!contributions) return null;
    const badge = contributions.badge || 'starter';
    const style = BADGE_STYLES[badge];
    const total = contributions.total_contributions || 0;
    const nextTier = badge === 'starter' ? 5 : badge === 'bronze' ? 20 : badge === 'silver' ? 50 : 100;
    const progress = Math.min((total / nextTier) * 100, 100);

    return (
      <div className="container mx-auto px-4 py-8">
        <Card className={`max-w-3xl mx-auto ${style.bg} ${style.border} border`} data-testid="founding-practitioner-card">
          <CardContent className="py-5 px-6">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${style.bg} border-2 ${style.border}`}>
                  <Award className={`w-7 h-7 ${style.icon}`} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className={`font-bold text-base ${style.text}`}>{contributions.badge_label || 'Founding Practitioner'}</h3>
                  <Badge variant="secondary" className={`text-[10px] ${style.bg} ${style.text} ${style.border}`}>{badge.toUpperCase()}</Badge>
                </div>
                <div className="mt-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-slate-500">{total} contributions</span>
                    <span className="text-xs text-slate-400">{nextTier - total > 0 ? `${nextTier - total} to next tier` : 'Max tier!'}</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
                <div className="flex gap-4 mt-2.5 text-xs text-slate-500">
                  <span>Feedback: {contributions.feedback_count || 0}</span>
                  <span>Protocols: {contributions.protocols_generated || 0}</span>
                  <span>Posts: {contributions.community_posts || 0}</span>
                  <span>Replies: {contributions.community_replies || 0}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const HomePage = () => (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #f0fdfa 0%, #f8fafc 30%, #ffffff 100%)' }}>
      {/* Hero */}
      <div className="container mx-auto px-4 md:px-6 pt-12 md:pt-20 pb-8">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <div className="flex flex-col items-center gap-5">
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl bg-teal-200/40 blur-2xl scale-125"></div>
              <img src="/images/dr_peptide_open_arms.png" alt="Dr. Peptide - the world's first clinical AI agent for peptide therapy" className="relative w-48 md:w-64 rounded-2xl shadow-lg border border-teal-200" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-emerald-700 mb-2">Peptide Education Hub</h2>
              <h1 className="text-3xl md:text-5xl font-bold text-slate-800 tracking-tight">Dr. Peptide <span className="text-teal-600">Self-Evolving Collective Intelligence</span> <span className="text-teal-600">Clinical AI Agent</span></h1>
              <p className="text-base md:text-lg text-teal-600 mt-2 font-medium">AI-Powered Peptide Protocol Intelligence for Practitioners</p>
            </div>
          </div>

          <p className="text-sm md:text-base text-slate-500 max-w-2xl mx-auto leading-relaxed">
            We analyze <span className="text-slate-800 font-semibold">your patient's labs</span>, <span className="text-slate-800 font-semibold">their genetics</span>, and <span className="text-slate-800 font-semibold">their health data</span> to create evidence-based protocols designed specifically for each patient.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <Button onClick={() => setCurrentView('chat')} size="lg" data-testid="hero-chat-cta" className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 text-base rounded-lg shadow-lg border border-teal-700">
              <MessageCircle className="h-5 w-5 mr-2" /> Chat with Dr. Peptide
            </Button>
            <Button onClick={() => setCurrentView('assessment')} size="lg" data-testid="hero-assessment-cta" className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-3 text-base rounded-lg shadow-lg border border-amber-600">
              <FileText className="h-5 w-5 mr-2" /> Generate Protocol
            </Button>
          </div>

          {/* SEO paragraph - visible crawlable text */}
          <p className="text-xs text-slate-400 max-w-2xl mx-auto leading-relaxed mt-4" data-testid="homepage-seo-paragraph">
            Dr. Peptide (also known as Doctor Peptide) is the world's first clinical AI agent for peptide protocols. Dr. Peptide uses 17 AI agents, multiple AI models (Dual-LLM + LRM), as well as live contributions and insight from a growing list of practitioners to analyze your patient's labs, genetics, and health data to create evidence-based peptide therapy protocols with predictive analytics and supporting studies.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="border-y border-teal-100 bg-teal-50/50">
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto">
            {[
              { value: "2,800+", label: "Studies Analyzed" },
              { value: "3,862", label: "Protocols Generated" },
              { value: "17 AI Agents", label: "2 LLMs and 1 LRM" },
              { value: "Practitioner", label: "Contributions, Advisory Board Oversight" },
              { value: "4+ Hours", label: "of Research Per Patient in 90 Seconds" },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-xl md:text-2xl font-bold text-teal-700">{s.value}</div>
                <div className="text-[10px] text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing tier comparison matrix */}
      <PricingComparisonMatrix />

      {/* Founding Practitioner Program */}
      <FoundingPractitionerCard />

      {/* Comparison */}
      <div className="container mx-auto px-4 py-14 md:py-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-slate-800 mb-2">Why Personalization Matters</h2>
          <p className="text-center text-slate-400 mb-10 text-sm">Every patient's biology is different. Generic stacks can't account for individual metabolic profiles.</p>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="rounded-xl p-6 bg-red-50 border border-red-200">
              <h4 className="font-bold text-red-700 mb-4 flex items-center gap-2"><XCircle className="h-5 w-5" /> Generic Chatbots</h4>
              <div className="space-y-2.5 text-sm text-red-600">
                {["One-size-fits-all stacks", "No lab interpretation", "No genetics analysis", "Generic dosing", "Basic Q&A"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2"><XCircle className="h-3.5 w-3.5 flex-shrink-0 text-red-400" /> {t}</div>
                ))}
              </div>
            </div>
            <div className="rounded-xl p-6 bg-teal-50 border border-teal-200">
              <h4 className="font-bold text-teal-700 mb-4 flex items-center gap-2"><CheckCircle className="h-5 w-5" /> PeptideProtocols.ai</h4>
              <div className="space-y-2.5 text-sm text-teal-700">
                {["Personalized to each patient's biology", "Lab-driven recommendations", "92-SNP genetics analysis", "Weight-based dosing calculations", "Dual-LLM + LRM Clinical Intelligence"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 flex-shrink-0 text-teal-500" /> {t}</div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <div className="rounded-lg p-4 bg-red-50/50 border border-red-100">
              <p className="text-xs font-semibold text-red-500 mb-1">Generic Chatbot:</p>
              <p className="text-sm text-slate-600 italic">"For longevity, try: Epithalon + GHK-Cu + MOTS-c"</p>
              <p className="text-xs text-slate-400 mt-1">Same for every patient</p>
            </div>
            <div className="rounded-lg p-4 bg-teal-50/50 border border-teal-100">
              <p className="text-xs font-semibold text-teal-600 mb-1">Dr. Peptide Analyzes:</p>
              <p className="text-sm text-slate-600 italic">"Based on your patient's <strong className="text-slate-800">MTHFR variant</strong> and <strong className="text-slate-800">elevated HbA1c (6.2%)</strong>, I recommend NAD+ + MOTS-c"</p>
              <p className="text-xs text-slate-400 mt-1">Personalized to patient's biology</p>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="border-y border-slate-100 bg-slate-50/50">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-slate-800 mb-10">Built for Practitioners</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {[
                { icon: Activity, title: "Personalized Protocols", desc: "Dual-LLM + LRM AI generates protocols from each patient's labs, genetics, and goals with guidance from advisors and contributing practitioners.", color: "text-teal-600", bg: "bg-teal-50" },
                { icon: FlaskConical, title: "Lab & Genetics Analysis", desc: "Upload labs, 23andMe data, and MaxGen reports. 92-SNP engine matches peptides to genetic profile.", color: "text-cyan-600", bg: "bg-cyan-50" },
                { icon: Users, title: "Practitioner Intelligence", desc: "Practitioners feed outcomes and feedback into AI, making it smarter over time.", color: "text-violet-600", bg: "bg-violet-50" },
                { icon: BookOpen, title: "Evidence-Based", desc: "Every recommendation backed by PubMed-cited clinical research.", color: "text-rose-600", bg: "bg-rose-50" },
                { icon: TrendingUp, title: "Patient Tracking", desc: "Track patients across visits. Progress scores, lab trends, protocols.", color: "text-amber-600", bg: "bg-amber-50" },
                { icon: Brain, title: "Self-Improving AI", desc: "Every feedback and outcome makes Dr. Peptide smarter.", color: "text-teal-600", bg: "bg-blue-50" },
              ].map((f, i) => (
                <div key={i} className={`rounded-xl p-5 bg-white border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all`}>
                  <div className={`h-10 w-10 ${f.bg} rounded-lg flex items-center justify-center mb-3`}>
                    <f.icon className={`h-5 w-5 ${f.color}`} />
                  </div>
                  <h3 className="font-semibold text-slate-800 text-sm mb-1">{f.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="container mx-auto px-4 py-14 md:py-20 text-center">
        <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">Ready to Transform Your Practice?</h3>
        <p className="text-slate-500 mb-8 text-sm">Join practitioners using AI-powered, personalized peptide protocols</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={() => setCurrentView('assessment')} size="lg" className="bg-teal-600 hover:bg-teal-700 text-white px-8 shadow-md">Start Assessment</Button>
          <Button onClick={() => setCurrentView('peptides')} size="lg" variant="outline" className="border-slate-300 text-slate-600 hover:bg-slate-50 px-8">Browse Peptides</Button>
          <Button onClick={() => setCurrentView('dashboard')} size="lg" variant="outline" className="border-slate-300 text-slate-600 hover:bg-slate-50 px-8">Patient Dashboard</Button>
        </div>
      </div>
    </div>
  );

  const AssessmentWizard = () => (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6 md:space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-2xl md:text-4xl font-bold">Intelligent Protocol Generator</h1>
        <p className="text-base md:text-lg text-gray-600">Advanced AI-powered peptide therapy protocols with evidence-based clinical recommendations.</p>
        
        <div className="flex justify-center gap-2 md:gap-4 text-sm">
          <Badge variant="outline">Evidence-Based</Badge>
          <Badge variant="outline">AI-Powered</Badge>
          <Badge variant="outline">Clinical Grade</Badge>
          {isAutoSaving && <Badge variant="outline" className="bg-blue-50">Auto-Saving...</Badge>}
        </div>
        
        {/* Auto-save status indicator */}
        <div className="text-xs text-gray-500 flex items-center justify-center gap-1">
          <CheckCircle className="h-3 w-3 text-green-500" />
          Your progress is automatically saved
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-gray-600">
          <span>Step {currentStep} of 6</span>
          <span>{Math.round((currentStep / 6) * 100)}% Complete</span>
        </div>
        <Progress value={(currentStep / 6) * 100} className="w-full" />
      </div>

      {/* Step Headers */}
      <div className="flex flex-wrap justify-center gap-2 text-xs">
        {['Personal Information', 'Health Goals & Objectives', 'Medical History', 'Lifestyle Assessment', 'Labs & Genetics (Optional)', 'Review & Generate'].map((step, index) => (
          <Badge key={index} variant={currentStep > index + 1 ? "default" : currentStep === index + 1 ? "secondary" : "outline"}>
            {step}
          </Badge>
        ))}
      </div>

      <Card className="border-2">
        <CardContent className="p-8">
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold">Patient Information</h2>
              <p className="text-gray-600">Enter patient demographics for personalized protocol development</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="patient_name">Patient Name *</Label>
                  <Input
                    ref={fullNameRef}
                    id="patient_name"
                    placeholder="Enter patient's full name"
                    autoComplete="off"
                    onBlur={(e) => handleAssessmentChange('patient_name', e.target.value)}
                    defaultValue={assessment.patient_name || ''}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="age">Patient Age *</Label>
                  <Input
                    ref={ageRef}
                    id="age"
                    type="number"
                    placeholder="Patient's age"
                    defaultValue={assessment.age}
                    onBlur={(e) => handleAssessmentChange('age', e.target.value)}
                    aria-label="Enter patient's age in years"
                    aria-describedby="age-description"
                  />
                  <span id="age-description" className="sr-only">Enter the patient's age in years</span>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="gender">Patient Gender *</Label>
                  <Select 
                    onValueChange={(value) => handleAssessmentChange('gender', value)} 
                    value={assessment.gender}
                    aria-label="Select patient's gender"
                  >
                    <SelectTrigger aria-label="Patient gender selection">
                      <SelectValue placeholder="Select patient gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                      <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Patient Weight (lbs) *</Label>
                  <Input
                    ref={weightRef}
                    type="number"
                    placeholder="Patient's weight in pounds"
                    onBlur={(e) => handleAssessmentChange('weight', e.target.value)}
                    defaultValue={assessment.weight || ''}
                    autoComplete="off"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Height *</Label>
                  <div className="flex gap-2">
                    <Select onValueChange={(value) => handleAssessmentChange('height_feet', value)} value={assessment.height_feet}>
                      <SelectTrigger>
                        <SelectValue placeholder="Feet" />
                      </SelectTrigger>
                      <SelectContent>
                        {[4,5,6,7].map(feet => (
                          <SelectItem key={feet} value={feet.toString()}>{feet} ft</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select onValueChange={(value) => handleAssessmentChange('height_inches', value)} value={assessment.height_inches}>
                      <SelectTrigger>
                        <SelectValue placeholder="Inches" />
                      </SelectTrigger>
                      <SelectContent>
                        {[0,1,2,3,4,5,6,7,8,9,10,11].map(inches => (
                          <SelectItem key={inches} value={inches.toString()}>{inches} in</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Patient Email Address *</Label>
                  <Input
                    ref={emailRef}
                    id="email"
                    type="email"
                    placeholder="patient@email.com"
                    defaultValue={assessment.email}
                    onBlur={(e) => handleAssessmentChange('email', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold">Patient Health Goals & Objectives</h2>
              
              <ListFieldInput
                field="primary_concerns"
                placeholder="e.g., Weight management, Low energy, Joint pain, Cognitive decline"
                label="Patient's Primary Health Concerns"
              />

              <ListFieldInput
                field="health_goals"
                placeholder="e.g., Lose 20 pounds, Increase energy, Better sleep, Anti-aging"
                label="Patient's Specific Health Goals"
              />
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold">Patient Medical History</h2>
              
              <ListFieldInput
                field="current_medications"
                placeholder="e.g., Metformin 500mg twice daily, Lisinopril 10mg daily"
                label="Patient's Current Medications & Supplements"
              />

              <ListFieldInput
                field="medical_history"
                placeholder="e.g., Type 2 diabetes, Hypertension, Previous injuries"
                label="Patient's Medical History & Conditions"
              />

              <ListFieldInput
                field="allergies"
                placeholder="e.g., Penicillin, Sulfa drugs, Food allergies"
                label="Patient's Known Allergies"
              />
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold">Patient Lifestyle Assessment</h2>
              <p className="text-gray-600">Patient lifestyle factors for personalized recommendations</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Exercise Frequency</Label>
                  <Select onValueChange={(value) => handleLifestyleFactorChange('exercise', value)} value={assessment.lifestyle_factors?.exercise}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sedentary">Sedentary (little to no exercise)</SelectItem>
                      <SelectItem value="light">Light (1-2 times per week)</SelectItem>
                      <SelectItem value="moderate">Moderate (3-4 times per week)</SelectItem>
                      <SelectItem value="active">Active (5-6 times per week)</SelectItem>
                      <SelectItem value="very_active">Very Active (daily)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Sleep Quality</Label>
                  <Select onValueChange={(value) => handleLifestyleFactorChange('sleep', value)} value={assessment.lifestyle_factors?.sleep}>
                    <SelectTrigger>
                      <SelectValue placeholder="Rate your sleep" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="poor">Poor (trouble sleeping, not rested)</SelectItem>
                      <SelectItem value="fair">Fair (some sleep issues)</SelectItem>
                      <SelectItem value="good">Good (generally sleep well)</SelectItem>
                      <SelectItem value="excellent">Excellent (sleep deeply, wake refreshed)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Stress Level</Label>
                  <Select onValueChange={(value) => handleLifestyleFactorChange('stress', value)} value={assessment.lifestyle_factors?.stress}>
                    <SelectTrigger>
                      <SelectValue placeholder="Rate your stress" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low stress</SelectItem>
                      <SelectItem value="moderate">Moderate stress</SelectItem>
                      <SelectItem value="high">High stress</SelectItem>
                      <SelectItem value="very_high">Very high stress</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Diet Type</Label>
                  <Select onValueChange={(value) => handleLifestyleFactorChange('diet', value)} value={assessment.lifestyle_factors?.diet}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select diet type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard American Diet</SelectItem>
                      <SelectItem value="mediterranean">Mediterranean</SelectItem>
                      <SelectItem value="keto">Ketogenic</SelectItem>
                      <SelectItem value="paleo">Paleo</SelectItem>
                      <SelectItem value="vegetarian">Vegetarian</SelectItem>
                      <SelectItem value="vegan">Vegan</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold">Lab Interpretation (Optional)</h2>
              <p className="text-gray-600">Paste your lab results for AI-powered analysis. Dr. Peptide will use this data to personalize your protocol recommendations.</p>
              
              <LabInterpretation
                onLabAnalysisComplete={(data) => {
                  setLabAnalysisData(data);
                  // Store lab data in assessment for protocol generation
                  setAssessment(prev => ({
                    ...prev,
                    lab_analysis: data
                  }));
                }}
                patientData={{
                  name: assessment.patient_name,
                  age: assessment.age,
                  gender: assessment.gender,
                  weight: assessment.weight,
                  medical_history: assessment.medical_history,
                  current_medications: assessment.current_medications
                }}
              />

              {labAnalysisData && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Lab analysis complete! Your results will be used to create a more personalized protocol.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {currentStep === 6 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold">Review & Generate</h2>
              <p className="text-gray-600">Review your information before generating your personalized protocol</p>
              
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-2">Personal Information</h3>
                      <p><strong>Name:</strong> {assessment.patient_name}</p>
                      <p><strong>Age:</strong> {assessment.age}</p>
                      <p><strong>Gender:</strong> {assessment.gender}</p>
                      <p><strong>Weight:</strong> {assessment.weight} {assessment.weight_unit || 'lbs'}</p>
                      {(assessment.height_feet || assessment.height_inches) ? (
                        <p><strong>Height:</strong> {assessment.height_feet}'{assessment.height_inches}"</p>
                      ) : (
                        <p><strong>Height:</strong> Not provided</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-2">Health Goals</h3>
                      <p><strong>Primary Concerns:</strong> {Array.isArray(assessment.primary_concerns) ? assessment.primary_concerns.join(', ') : assessment.primary_concerns || 'None specified'}</p>
                      <p><strong>Goals:</strong> {Array.isArray(assessment.health_goals) ? assessment.health_goals.join(', ') : assessment.health_goals || 'None specified'}</p>
                    </CardContent>
                  </Card>
                </div>

                <Button 
                  onClick={generateFunctionalProtocol}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-teal-600 to-teal-500 hover:from-blue-700 hover:to-purple-700 py-4"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {protocolProgress || "Generating Your Personalized Protocol..."}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Zap className="h-5 w-5" />
                      Generate Personalized Protocol
                    </div>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-8">
            <Button 
              variant="outline" 
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1 || loading}
              aria-label={`Go to previous step ${currentStep - 1 > 0 ? currentStep - 1 : ''}`}
              aria-describedby="nav-help"
            >
              Previous
            </Button>
            <Button 
              className="px-8"
              onClick={() => {
                if (currentStep < 6) {
                  
                  // Collect data from refs for Step 1 (Personal Information)
                  let updatedAssessment = { ...assessment };
                  if (currentStep === 1) {
                    updatedAssessment = {
                      ...assessment,
                      patient_name: fullNameRef.current?.value || '',
                      weight: weightRef.current?.value || ''
                    };
                    setAssessment(updatedAssessment);
                  }
                  
                  // Advance to next step
                  setCurrentStep(currentStep + 1);
                  
                  // Save in background (don't block progression)
                  saveAssessmentStep(currentStep, updatedAssessment).catch(error => {
                    console.error('Background save error:', error);
                  });
                }
              }}
              disabled={currentStep === 6 || loading}
              aria-label={`Proceed to ${currentStep < 6 ? `step ${currentStep + 1}` : 'generate protocol'}`}
              aria-describedby="nav-help"
            >
              Next
            </Button>
            <span id="nav-help" className="sr-only">Use Previous and Next buttons to navigate through the assessment steps</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const DrPeptideChat = () => (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
      <Card className="h-[500px] md:h-[600px] flex flex-col">
        <CardHeader className="px-4 md:px-6">
          <CardTitle className="flex items-center gap-2">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Brain className="h-6 w-6 md:h-8 md:w-8 text-teal-600" />
                <div className="absolute -top-1 -right-1 bg-green-400 rounded-full p-1">
                  <Stethoscope className="h-3 w-3 md:h-4 md:w-4 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-lg md:text-xl font-bold">Dr. Peptide AI</h1>
                <p className="text-xs md:text-sm text-gray-600">Your Functional Medicine Expert</p>
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        
        <CardContent className="flex-1 flex flex-col">
          {/* Clear Chat Button */}
          {chatMessages.length > 0 && (
            <div className="mb-2 flex justify-end">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  if (confirm("Are you sure you want to clear the conversation history?")) {
                    setChatMessages([]);
                    setPatientContext('');
                    setUploadedLabFiles([]);
                  }
                }}
                className="text-gray-500 hover:text-red-600"
              >
                🗑️ Clear Chat
              </Button>
            </div>
          )}
          
          {/* Patient Context Section */}
          {showPatientInfo && (
            <div className="mb-4 p-4 border rounded-lg bg-blue-50 border-blue-200">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Patient Information
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowPatientInfo(false)}
                >
                  ✕
                </Button>
              </div>
              {/* Patient Information Section with Drag & Drop */}
              <textarea
                ref={patientContextRef}
                className="w-full p-2 border rounded text-sm mb-2 min-h-[100px]"
                placeholder="Enter patient details (age, sex, weight, medical history, current medications, labs, symptoms, etc.)..."
                rows={3}
                defaultValue={patientContext}
                onBlur={(e) => {
                  setPatientContext(e.target.value);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                }}
                onDrop={async (e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                  
                  const files = Array.from(e.dataTransfer.files);
                  if (files.length > 0) {
                    setUploadedLabFiles(prev => [...prev, ...files]);
                    await handleFileUpload(files, 'labs');
                  }
                }}
              />
              
              {/* File Upload Section with Enhanced UI */}
              <div className="space-y-3">
                <div className="flex gap-2 items-center flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={uploadingFiles}
                    onClick={() => document.getElementById('lab-file-input').click()}
                    className="flex items-center gap-2"
                  >
                    {uploadingFiles ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        Upload Lab Results
                      </>
                    )}
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={uploadingFiles}
                    onClick={() => document.getElementById('genetics-file-input').click()}
                    className="flex items-center gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    {uploadingFiles ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                        Analyzing...
                      </>
                    ) : (
                      <>
                        🧬 Upload Genetic Tests
                      </>
                    )}
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowTemplateModal(true)}
                    className="flex items-center gap-2"
                  >
                    📋 Quick Entry
                  </Button>
                  
                  <div className="text-xs text-gray-500 ml-2">
                    💡 Drag & drop files here or use buttons
                  </div>
                  
                  <input 
                    id="lab-file-input" 
                    type="file" 
                    className="hidden" 
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.txt"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > 0) {
                        setUploadedLabFiles(prev => [...prev, ...files]);
                        await handleFileUpload(files, 'labs');
                      }
                      e.target.value = '';
                    }}
                  />
                  
                  <input 
                    id="genetics-file-input" 
                    type="file" 
                    className="hidden" 
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.txt,.vcf,.23andme"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > 0) {
                        setUploadedLabFiles(prev => [...prev, ...files]);
                        await handleFileUpload(files, 'genetics');
                      }
                      e.target.value = '';
                    }}
                  />
                </div>
                
                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded p-3 space-y-2">
                    <div className="text-sm font-medium text-green-800">
                      📎 Attached Files ({uploadedFiles.length})
                    </div>
                    {uploadedFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm bg-white rounded p-2 border border-green-200">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">📄</span>
                          <span className="font-medium">{file.filename}</span>
                          {file.filesize && (
                            <span className="text-gray-500">({(file.filesize / 1024).toFixed(1)} KB)</span>
                          )}
                        </div>
                        <button
                          onClick={() => removeUploadedFile(file.filename)}
                          className="text-red-600 hover:text-red-800 font-bold"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {patientContext && patientContext.length > 50 && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-2"
                    onClick={async () => {
                      try {
                        setChatLoading(true);
                        const response = await axios.post(`${BACKEND_URL}/api/labs/interpret`, {
                          text: patientContext
                        }, { timeout: 60000 });
                        
                        if (response.data.success) {
                          const analysis = response.data.analysis;
                          const interpretation = response.data.ai_interpretation;
                          
                          let labSummary = `\n🔬 **Lab Analysis Complete**\n\n`;
                          labSummary += `**Health Score:** ${analysis.health_score}/100\n`;
                          labSummary += `**Tests:** ${analysis.total_tests} total, ${analysis.abnormal_count} abnormal, ${analysis.critical_count} critical\n\n`;
                          
                          // Abnormal results
                          const abnormal = analysis.results.filter(r => r.status !== 'normal');
                          if (abnormal.length > 0) {
                            labSummary += `**Abnormal Results:**\n`;
                            abnormal.forEach(r => {
                              labSummary += `${r.flag} ${r.display_name}: ${r.value} ${r.unit} (Normal: ${r.normal_range})\n`;
                            });
                            labSummary += `\n`;
                          }
                          
                          // Recommendations
                          if (analysis.recommendations && analysis.recommendations.length > 0) {
                            labSummary += `**Recommendations:**\n`;
                            analysis.recommendations.forEach(rec => {
                              labSummary += `• ${rec}\n`;
                            });
                          }
                          
                          labSummary += `\n**Clinical Interpretation:**\n${interpretation}`;
                          
                          setChatMessages(prev => [...prev, {
                            role: 'assistant',
                            content: labSummary
                          }]);
                        } else {
                          alert('Could not interpret labs. Try formatting as: "Test Name: Value Unit"');
                        }
                      } catch (err) {
                        console.error('Lab interpretation error:', err);
                        alert('Lab interpretation error. Ensure patient info contains lab values.');
                      } finally {
                        setChatLoading(false);
                      }
                    }}
                  >
                    🔬 Interpret Labs
                  </Button>
                )}
              </div>
            </div>
          )}
          
          {!showPatientInfo && (
            <Button 
              variant="outline" 
              size="sm" 
              className="mb-4"
              onClick={() => setShowPatientInfo(true)}
            >
              <FileText className="h-4 w-4 mr-2" />
              Show Patient Info
            </Button>
          )}
          
          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto space-y-4 mb-4 p-4 border rounded-lg bg-gray-50">
            {chatMessages.length === 0 && (
              <div className="text-center text-gray-500 py-8">
                <Brain className="h-12 w-12 mx-auto mb-4 text-blue-400" />
                <p>👋 Hello! I'm Dr. Peptide, your AI clinical partner for peptide therapy.</p>
                <p className="text-sm mt-2">I can help you with patient protocols, lab interpretation, and personalized health optimization strategies.</p>
                <p className="text-xs mt-2 text-teal-600">💡 Add patient context above for personalized clinical recommendations</p>
              </div>
            )}
            
            {chatMessages.map((message, index) => (
              <div key={index} className={`flex flex-col ${message.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-lg ${
                  message.role === 'user' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white border shadow-sm'
                }`}>
                  {message.role === 'assistant' && (
                    <div className="flex items-center gap-2 mb-2">
                      <Brain className="h-4 w-4 text-teal-600" />
                      <span className="text-sm font-semibold text-teal-600">Dr. Peptide</span>
                    </div>
                  )}
                  {message.role === 'assistant' ? (
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                        a: ({href, children}) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-800 underline">{children}</a>,
                        strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                        p: ({children}) => <p className="text-sm leading-relaxed mb-1.5">{children}</p>,
                        h2: ({children}) => <h3 className="text-base font-semibold text-teal-700 mt-2 mb-1">{children}</h3>,
                        h3: ({children}) => <h4 className="text-sm font-semibold text-gray-800 mt-2 mb-1">{children}</h4>,
                        ul: ({children}) => <ul className="list-disc pl-5 space-y-0.5 my-1">{children}</ul>,
                        ol: ({children}) => <ol className="list-decimal pl-5 space-y-0.5 my-1">{children}</ol>,
                        li: ({children}) => <li className="text-sm leading-relaxed">{children}</li>,
                        table: ({children}) => <div className="overflow-x-auto my-2"><table className="min-w-full text-sm border-collapse border border-gray-300">{children}</table></div>,
                        thead: ({children}) => <thead className="bg-teal-50">{children}</thead>,
                        th: ({children}) => <th className="border border-gray-300 px-2 py-1 text-left font-semibold text-xs">{children}</th>,
                        td: ({children}) => <td className="border border-gray-300 px-2 py-1 text-xs">{children}</td>,
                      }}>{typeof message.content === 'string' ? message.content : String(message.content)}</ReactMarkdown>
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap">{message.content}</div>
                  )}
                </div>
                
                {/* Action Buttons for Assistant Messages */}
                {message.role === 'assistant' && index === chatMessages.length - 1 && !chatLoading && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={async () => {
                        // Extract key terms from last user message for research
                        const lastUserMsg = chatMessages.filter(m => m.role === 'user').slice(-1)[0];
                        const query = lastUserMsg ? lastUserMsg.content.substring(0, 100) : message.content.substring(0, 100);
                        
                        setChatLoading(true);
                        try {
                          const response = await axios.post(`${BACKEND_URL}/api/dr-peptide/deep-research`, {
                            query: query,
                            max_results: 10,
                            days_back: 730
                          }, { timeout: 60000 });
                          
                          if (response.data.success) {
                            const results = response.data.results;
                            let researchSummary = `\n\n🔬 **Deep Research Results** (${results.total_results} studies found in ${results.execution_time_ms}ms)\n\n`;
                            
                            // PubMed results
                            if (results.pubmed && results.pubmed.length > 0) {
                              researchSummary += `**Recent PubMed Studies:**\n`;
                              results.pubmed.slice(0, 5).forEach((study, i) => {
                                researchSummary += `${i+1}. ${study.authors} (${study.year}) - "${study.title}"\n   ${study.journal} | PMID: ${study.pmid}\n\n`;
                              });
                            }
                            
                            // Scholar results
                            if (results.scholar && results.scholar.length > 0) {
                              researchSummary += `\n**Academic Papers:**\n`;
                              results.scholar.slice(0, 3).forEach((paper, i) => {
                                researchSummary += `${i+1}. ${paper.title} (${paper.year})\n   Cited by: ${paper.citations}\n\n`;
                              });
                            }
                            
                            // Clinical trials
                            if (results.trials && results.trials.length > 0) {
                              researchSummary += `\n**Active Clinical Trials:**\n`;
                              results.trials.forEach((trial, i) => {
                                researchSummary += `${i+1}. ${trial.title}\n   Status: ${trial.status} | Phase: ${trial.phase} | ID: ${trial.trial_id}\n\n`;
                              });
                            }
                            
                            setChatMessages(prev => [...prev, {
                              role: 'assistant',
                              content: researchSummary
                            }]);
                          } else {
                            alert('Deep research temporarily unavailable. Please try again.');
                          }
                        } catch (err) {
                          console.error('Deep research error:', err);
                          alert('Deep research error. Please check API keys in backend .env file.');
                        } finally {
                          setChatLoading(false);
                        }
                      }}
                      className="bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700"
                    >
                      🔬 Deep Research
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        chatInputRef.current.focus();
                      }}
                    >
                      💬 Ask Another Question
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const followUp = "Can you clarify or expand on your last answer?";
                        chatWithDrPeptide(followUp);
                      }}
                    >
                      ❓ Ask About This Answer
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={async () => {
                        const feedback = prompt("Please provide your feedback on this response (corrections, improvements, accuracy issues):");
                        if (feedback && feedback.trim()) {
                          try {
                            const response = await axios.post(`${BACKEND_URL}/api/dr-peptide/feedback`, {
                              message: message.content,
                              feedback: feedback.trim(),
                              timestamp: new Date().toISOString()
                            });
                            alert("✅ Thank you! Your feedback helps Dr. Peptide improve.");
                          } catch (err) {
                            console.error("Feedback error:", err);
                            alert("✅ Feedback saved! Thank you for helping improve Dr. Peptide.");
                          }
                        }
                      }}
                    >
                      ⭐ Provide Feedback
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(message.content);
                          alert("✅ Response copied to clipboard!");
                        } catch (err) {
                          console.error("Copy error:", err);
                          // Fallback for older browsers
                          const textArea = document.createElement("textarea");
                          textArea.value = message.content;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                          alert("✅ Response copied to clipboard!");
                        }
                      }}
                    >
                      📋 Copy
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        try {
                          window.print();
                        } catch (err) {
                          console.error("Print error:", err);
                          alert("Print failed. Please use your browser's print function (Ctrl+P or Cmd+P)");
                        }
                      }}
                    >
                      🖨️ Print
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={async () => {
                        try {
                          // Check if Web Share API is available
                          if (navigator.share) {
                            await navigator.share({
                              title: 'Dr. Peptide Response',
                              text: message.content,
                              url: window.location.href
                            });
                          } else {
                            // Fallback: Copy to clipboard and show message
                            await navigator.clipboard.writeText(message.content);
                            alert("✅ Response copied to clipboard! You can now paste and share it.");
                          }
                        } catch (err) {
                          console.error("Share error:", err);
                          // If sharing fails, try clipboard as backup
                          try {
                            await navigator.clipboard.writeText(message.content);
                            alert("✅ Response copied to clipboard! You can now paste and share it.");
                          } catch (clipErr) {
                            alert("Unable to share. Please use the Copy button instead.");
                          }
                        }
                      }}
                    >
                      🔗 Share
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={generatingPatientSummary}
                      onClick={() => generatePatientSummary(message.content)}
                      className="border-green-300 text-green-700 hover:bg-green-50"
                    >
                      {generatingPatientSummary ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600 mr-1"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          👤 Patient Summary
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            ))}
            
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-white border shadow-sm p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Brain className="h-4 w-4 text-teal-600" />
                    <span className="text-sm">Dr. Peptide is thinking...</span>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Chat Input - Enhanced */}
          <div className="border-t-2 pt-6 bg-gradient-to-b from-white to-blue-50 px-4 pb-4">
            <div className="space-y-3">
              <Textarea
                ref={chatInputRef}
                placeholder="Ask Dr. Peptide about peptides, labs, protocols, or health optimization for your patient..."
                autoComplete="off"
                disabled={chatLoading}
                rows={12}
                className="w-full resize-y text-base leading-relaxed p-4 min-h-[300px] border-2 focus:border-blue-500 shadow-lg"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !chatLoading) {
                    e.preventDefault();
                    const inputValue = chatInputRef.current?.value?.trim();
                    if (inputValue) {
                      const messageWithContext = patientContext 
                        ? `PATIENT CONTEXT: ${patientContext}\n\nQUESTION: ${inputValue}`
                        : inputValue;
                      chatWithDrPeptide(messageWithContext);
                      chatInputRef.current.value = '';
                    }
                  }
                }}
              />
              
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm text-gray-500 px-2">
                  💡 Press Enter to send • Shift+Enter for new line
                </div>
                <Button 
                  onClick={() => {
                    const inputValue = chatInputRef.current?.value?.trim();
                    if (inputValue) {
                      const messageWithContext = patientContext 
                        ? `PATIENT CONTEXT: ${patientContext}\n\nQUESTION: ${inputValue}`
                        : inputValue;
                      chatWithDrPeptide(messageWithContext);
                      chatInputRef.current.value = '';
                    }
                  }}
                  disabled={chatLoading}
                  size="lg"
                  className="px-12 py-6 text-lg"
                >
                  {chatLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="h-5 w-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            <div className="text-sm text-gray-600 mt-5 text-center px-6 py-4 bg-yellow-50 rounded-lg border-2 border-yellow-300 shadow-sm">
              ⚕️ <strong>Professional Use Only:</strong> Educational information only. Always consult healthcare provider for medical decisions.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const ProtocolLibrary = () => {
    const [protocols, setProtocols] = useState([]);
    const [filteredProtocols, setFilteredProtocols] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedTags, setSelectedTags] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableTags, setAvailableTags] = useState([]);
    const [libraryStats, setLibraryStats] = useState(null);
    const [selectedProtocol, setSelectedProtocol] = useState(null);
    const [sortBy, setSortBy] = useState('name');
    const [viewMode, setViewMode] = useState('grid'); // grid or list
    const [bookmarks, setBookmarks] = useState(getBookmarks());      // array of bookmarked protocol IDs
    const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

    // Load protocols and metadata
    useEffect(() => {
      loadProtocolLibrary();
      // Cross-device bookmark sync: on boot (which only mounts this view once the
      // user is past AuthGate, i.e. pp_auth is present), pull the account's
      // server-side bookmarks and adopt them as the source of truth — see the
      // REGRESSION NOTE in lib/bookmarks.js syncBookmarksFromServer(). Server
      // response overwrites local cache; toggleBookmark() then mirrors every
      // add/remove back to the server to keep them in step. No-op when anonymous.
      syncBookmarksFromServer().then(setBookmarks);
    }, []);

    const handleToggleBookmark = (id) => {
      setBookmarks(toggleBookmarkStore(id));
    };

    // Filter protocols when search/filter changes
    useEffect(() => {
      filterProtocols();
    }, [protocols, searchQuery, selectedCategory, selectedTags, sortBy]);

    const loadProtocolLibrary = async () => {
      try {
        setLoading(true);
        
        // Load protocols, categories, tags, and stats in parallel
        const [protocolsRes, categoriesRes, tagsRes, statsRes] = await Promise.all([
          axios.get(`${API}/protocols/library/search?limit=100`),
          axios.get(`${API}/protocols/library/categories`),
          axios.get(`${API}/protocols/library/tags`),
          axios.get(`${API}/protocols/library/stats`)
        ]);
        
        setProtocols(protocolsRes.data.protocols || []);
        setFilteredProtocols(protocolsRes.data.protocols || []);
        setAvailableCategories(categoriesRes.data.categories || []);
        setAvailableTags(tagsRes.data.tags || []);
        setLibraryStats(statsRes.data.library_stats || null);
        
      } catch (error) {
        console.error('❌ Error loading protocol library:', error);
      } finally {
        setLoading(false);
      }
    };

    const filterProtocols = async () => {
      if (protocols.length === 0) return;
      
      try {
        // If we have active search/filters, use the search API
        if (searchQuery || selectedCategory !== 'all' || selectedTags.length > 0) {
          const params = new URLSearchParams();
          if (searchQuery) params.append('query', searchQuery);
          if (selectedCategory !== 'all') params.append('category', selectedCategory);
          if (selectedTags.length > 0) params.append('tags', selectedTags.join(','));
          params.append('limit', '100');
          
          const response = await axios.get(`${API}/protocols/library/search?${params}`);
          let filtered = response.data.protocols || [];
          
          // Apply client-side sorting
          filtered = filtered.sort((a, b) => {
            switch (sortBy) {
              case 'name':
                return (a.name || '').localeCompare(b.name || '');
              case 'category':
                return (a.category || '').localeCompare(b.category || '');
              case 'rating':
                return (b.outcome_stats?.efficacy_rating || 0) - (a.outcome_stats?.efficacy_rating || 0);
              case 'updated':
                return new Date(b.last_updated || 0) - new Date(a.last_updated || 0);
              default:
                return 0;
            }
          });
          
          setFilteredProtocols(filtered);
        } else {
          // No filters, show all protocols
          setFilteredProtocols(protocols);
        }
      } catch (error) {
        console.error('Error filtering protocols:', error);
        // Fallback to client-side filtering
        setFilteredProtocols(protocols);
      }
    };

    const toggleTag = (tag) => {
      setSelectedTags(prev => 
        prev.includes(tag) 
          ? prev.filter(t => t !== tag)
          : [...prev, tag]
      );
    };

    const downloadProtocolPDF = async (protocol) => {
      try {
        const response = await axios.get(`${API}/protocols/${protocol.id}/pdf`, {
          responseType: 'blob'
        });
        
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${protocol.name.replace(/\s+/g, '_')}_Protocol.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error('Error downloading PDF:', error);
      }
    };

    const ProtocolCard = ({ protocol }) => (
      <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer group" 
            onClick={() => setSelectedProtocol(protocol)}>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-start">
            <CardTitle className="text-lg group-hover:text-teal-600 transition-colors">
              {protocol.name}
            </CardTitle>
            <div className="flex flex-col items-end gap-1">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleToggleBookmark(protocol.id); }}
                aria-label={bookmarks.includes(protocol.id) ? 'Remove bookmark' : 'Add bookmark'}
                aria-pressed={bookmarks.includes(protocol.id)}
                className="text-slate-400 hover:text-teal-600 transition-colors"
                data-testid={`bookmark-btn-${protocol.id}`}
              >
                {bookmarks.includes(protocol.id)
                  ? <Bookmark className="h-4 w-4 fill-teal-600 text-teal-600" />
                  : <BookmarkPlus className="h-4 w-4" />}
              </button>
              <Badge variant="outline" className="text-xs">
                {protocol.category}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600 line-clamp-3">
            {protocol.description || 'No description available'}
          </p>
          
          {/* Clinical Indications */}
          {protocol.clinical_indications && protocol.clinical_indications.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm mb-2">Indications:</h4>
              <div className="flex flex-wrap gap-1">
                {protocol.clinical_indications.slice(0, 3).map((indication, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {indication}
                  </Badge>
                ))}
                {protocol.clinical_indications.length > 3 && (
                  <Badge variant="secondary" className="text-xs">
                    +{protocol.clinical_indications.length - 3} more
                  </Badge>
                )}
              </div>
            </div>
          )}
          
          {/* Tags */}
          {protocol.tags && protocol.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {protocol.tags.slice(0, 4).map((tag, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button size="sm" className="flex-1">
              View Details
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={(e) => {
                e.stopPropagation();
                downloadProtocolPDF(protocol);
              }}
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Last Updated */}
          {protocol.last_updated && (
            <div className="flex items-center gap-1 text-xs text-gray-500 pt-2 border-t">
              <Clock className="h-3 w-3" />
              Updated {new Date(protocol.last_updated).toLocaleDateString()}
            </div>
          )}
        </CardContent>
      </Card>
    );

    if (loading) {
      return (
        <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-8">
          <div className="text-center">
            <h1 className="text-2xl md:text-4xl font-bold">Clinical Protocol Library</h1>
            <p className="text-base md:text-lg text-gray-600 mt-2">Loading comprehensive protocols...</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-8 bg-gray-200 rounded w-full"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      );
    }

    // Apply the "Bookmarked only" chip on top of the search/category/tag filters.
    const displayProtocols = showBookmarkedOnly
      ? filteredProtocols.filter((p) => bookmarks.includes(p.id))
      : filteredProtocols;

    return (
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6 md:space-y-8">
        {/* Header with Stats */}
        <div className="text-center space-y-4">
          <h1 className="text-2xl md:text-4xl font-bold">Clinical Protocol Library</h1>
          <p className="text-base md:text-lg text-gray-600">
            Browse our comprehensive collection of evidence-based peptide therapy protocols
          </p>
          {libraryStats && (
            <div className="flex justify-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <FileText className="h-4 w-4" />
                {libraryStats.total_protocols} Protocols
              </div>
              <div className="flex items-center gap-1">
                <Filter className="h-4 w-4" />
                {libraryStats.categories} Categories
              </div>
            </div>
          )}
        </div>

        {/* Search and Filters */}
        <div className="space-y-4">
          {/* Main Search Bar */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
              <Input
                placeholder="Search protocols by name, indication, or mechanism..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Select onValueChange={setSelectedCategory} value={selectedCategory}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {availableCategories.map(category => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select onValueChange={setSortBy} value={sortBy}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="category">Category</SelectItem>
                  <SelectItem value="updated">Updated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Bookmarked filter chip */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowBookmarkedOnly(v => !v)}
              aria-pressed={showBookmarkedOnly}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                showBookmarkedOnly
                  ? 'bg-teal-600 text-white border-teal-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'
              }`}
              data-testid="bookmarked-filter-chip"
            >
              <Bookmark className={`h-3.5 w-3.5 ${showBookmarkedOnly ? 'fill-white' : ''}`} />
              Bookmarked{bookmarks.length > 0 ? ` (${bookmarks.length})` : ''}
            </button>
          </div>

          {/* Tag Filters */}
          {availableTags.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Filter by Tags:</h3>
              <div className="flex flex-wrap gap-2">
                {availableTags.slice(0, 12).map(tag => (
                  <Badge
                    key={tag}
                    variant={selectedTags.includes(tag) ? "default" : "outline"}
                    className="cursor-pointer hover:bg-blue-100"
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Active Filters Display */}
          {(selectedCategory !== 'all' || selectedTags.length > 0 || searchQuery) && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">Active filters:</span>
              {searchQuery && (
                <Badge variant="secondary">
                  Search: "{searchQuery}"
                  <button 
                    className="ml-1 hover:text-red-600"
                    onClick={() => setSearchQuery('')}
                  >
                    ×
                  </button>
                </Badge>
              )}
              {selectedCategory !== 'all' && (
                <Badge variant="secondary">
                  Category: {selectedCategory}
                  <button 
                    className="ml-1 hover:text-red-600"
                    onClick={() => setSelectedCategory('all')}
                  >
                    ×
                  </button>
                </Badge>
              )}
              {selectedTags.map(tag => (
                <Badge key={tag} variant="secondary">
                  {tag}
                  <button 
                    className="ml-1 hover:text-red-600"
                    onClick={() => toggleTag(tag)}
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Results Summary */}
        <div className="flex justify-between items-center">
          <p className="text-gray-600">
            Showing {displayProtocols.length} of {protocols.length} protocols
          </p>
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              Grid
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              List
            </Button>
          </div>
        </div>

        {/* Protocol Grid/List */}
        {displayProtocols.length > 0 ? (
          <div className={
            viewMode === 'grid'
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
              : "space-y-4"
          }>
            {displayProtocols.map((protocol) => (
              <ProtocolCard key={protocol.id} protocol={protocol} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No protocols found</h3>
            <p className="text-gray-500 mb-4">
              Try adjusting your search criteria or filters
            </p>
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
                setSelectedTags([]);
                setShowBookmarkedOnly(false);
              }}
            >
              Clear All Filters
            </Button>
          </div>
        )}

        {/* Detailed Protocol Modal */}
        {selectedProtocol && (
          <ProtocolDetailModal 
            protocol={selectedProtocol} 
            onClose={() => setSelectedProtocol(null)}
            onDownloadPDF={downloadProtocolPDF}
          />
        )}
      </div>
    );
  };

  // Collapsible accordion item used inside the Protocol "View Details" modal.
  // Body stays in the DOM (toggled via CSS) so window.print() can force every
  // section open with the `print:block` modifier regardless of collapse state.
  const ProtoSection = ({ title, icon, open, onToggle, children, testid }) => (
    <section className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="protocol-no-print w-full flex items-center justify-between gap-2 p-4 text-left hover:bg-gray-50 transition-colors"
        data-testid={testid}
      >
        <h3 className="text-xl font-semibold flex items-center gap-2">
          {icon}
          {title}
        </h3>
        {open ? <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />}
      </button>
      <div className={`${open ? 'block' : 'hidden'} print:block px-4 pb-4`}>
        {children}
      </div>
    </section>
  );

  const ProtocolDetailModal = ({ protocol, onClose, onDownloadPDF }) => {
    // Sections that can appear in the modal. Default state: all collapsed except
    // the first (Protocol Overview) so the most important info shows immediately
    // and users discover the accordion pattern.
    const SECTION_IDS = ['overview', 'indications', 'mechanism', 'dosing', 'administration', 'safety', 'monitoring', 'references', 'studies'];
    const [openSections, setOpenSections] = useState({ overview: true });
    const [bookmarked, setBookmarked] = useState(isBookmarkedStore(protocol.id));

    const handleToggleBookmark = () => {
      const next = toggleBookmarkStore(protocol.id);
      setBookmarked(next.includes(protocol.id));
    };

    const toggleSection = (id) => setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
    const expandAll = () => setOpenSections(Object.fromEntries(SECTION_IDS.map(id => [id, true])));
    const collapseAll = () => setOpenSections({});

    const handlePrint = () => {
      // Force every section open before printing, then print on the next tick so
      // the DOM reflects the expanded state (print CSS also forces this as a safety net).
      expandAll();
      setTimeout(() => window.print(), 50);
    };

    return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <Card className="protocol-print-area max-w-6xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="sticky top-0 bg-white border-b z-10">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <CardTitle className="text-3xl">{protocol.name}</CardTitle>
              <div className="flex items-center gap-3 mt-2">
                <Badge variant="default">{protocol.category}</Badge>
              </div>
            </div>
            <div className="flex items-center gap-2 protocol-no-print">
              <Button
                variant="outline"
                onClick={handleToggleBookmark}
                size="sm"
                aria-pressed={bookmarked}
                data-testid="protocol-bookmark-btn"
              >
                {bookmarked
                  ? <Bookmark className="h-4 w-4 sm:mr-1.5 fill-teal-600 text-teal-600" />
                  : <BookmarkPlus className="h-4 w-4 sm:mr-1.5" />}
                <span className="hidden sm:inline">{bookmarked ? 'Saved' : 'Save'}</span>
              </Button>
              <Button variant="outline" onClick={handlePrint} size="sm" data-testid="protocol-print-btn">
                <Printer className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Print</span>
              </Button>
              <Button variant="ghost" onClick={onClose} size="sm">
                <XCircle className="h-5 w-5" />
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-3 protocol-no-print">
            <button type="button" onClick={expandAll} className="text-xs font-medium text-teal-600 hover:text-teal-800" data-testid="protocol-expand-all">Expand All</button>
            <span className="text-gray-300">|</span>
            <button type="button" onClick={collapseAll} className="text-xs font-medium text-teal-600 hover:text-teal-800" data-testid="protocol-collapse-all">Collapse All</button>
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-4">
          {/* Protocol Overview */}
          <ProtoSection title="Protocol Overview" icon={<FileText className="h-5 w-5 text-teal-600" />} open={!!openSections.overview} onToggle={() => toggleSection('overview')} testid="protocol-section-overview">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-1">Description</h4>
                  <p className="text-gray-600">{protocol.description || 'No description available'}</p>
                </div>
                {protocol.sequence && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-1">Peptide Sequence</h4>
                    <p className="font-mono text-sm bg-gray-50 p-2 rounded">{protocol.sequence}</p>
                  </div>
                )}
                {protocol.molecular_weight && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-1">Molecular Weight</h4>
                    <p className="text-gray-600">{protocol.molecular_weight} Da</p>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                {protocol.aliases && protocol.aliases.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-1">Also Known As</h4>
                    <div className="flex flex-wrap gap-1">
                      {protocol.aliases.map((alias, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {alias}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {protocol.tags && protocol.tags.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-1">Tags</h4>
                    <div className="flex flex-wrap gap-1">
                      {protocol.tags.map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ProtoSection>

          {/* Clinical Indications */}
          {protocol.clinical_indications && protocol.clinical_indications.length > 0 && (
            <ProtoSection title="Clinical Indications" icon={<Activity className="h-5 w-5 text-green-600" />} open={!!openSections.indications} onToggle={() => toggleSection('indications')} testid="protocol-section-indications">
              <div className="grid md:grid-cols-2 gap-2">
                {protocol.clinical_indications.map((indication, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-3 bg-green-50 rounded-lg">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{indication}</span>
                  </div>
                ))}
              </div>
            </ProtoSection>
          )}

          {/* Mechanism of Action */}
          {protocol.mechanism_of_action && (
            <ProtoSection title="Mechanism of Action" icon={<Zap className="h-5 w-5 text-purple-600" />} open={!!openSections.mechanism} onToggle={() => toggleSection('mechanism')} testid="protocol-section-mechanism">
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-gray-700 leading-relaxed">{protocol.mechanism_of_action}</p>
              </div>
            </ProtoSection>
          )}

          {/* Dosing Protocols */}
          {protocol.complete_dosing_schedule && (
            <ProtoSection title="Dosing Protocols" icon={<FlaskConical className="h-5 w-5 text-orange-600" />} open={!!openSections.dosing} onToggle={() => toggleSection('dosing')} testid="protocol-section-dosing">
              <div className="bg-orange-50 p-4 rounded-lg">
                {typeof protocol.complete_dosing_schedule === 'object' ? (
                  <div className="space-y-3">
                    {Object.entries(protocol.complete_dosing_schedule).map(([key, value]) => (
                      <div key={key} className="flex justify-between items-start border-b border-orange-200 pb-2 last:border-b-0">
                        <span className="font-semibold text-orange-800 capitalize">
                          {key.replace(/_/g, ' ')}:
                        </span>
                        <span className="text-gray-700 text-right flex-1 ml-4">{value}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-700">{protocol.complete_dosing_schedule}</p>
                )}
              </div>
            </ProtoSection>
          )}

          {/* Administration Guidelines */}
          {protocol.administration_techniques && (
            <ProtoSection title="Administration Guidelines" icon={<User className="h-5 w-5 text-teal-600" />} open={!!openSections.administration} onToggle={() => toggleSection('administration')} testid="protocol-section-administration">
              <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                {Object.entries(protocol.administration_techniques).map(([key, value]) => (
                  <div key={key}>
                    <h4 className="font-semibold text-blue-800 capitalize mb-1">
                      {key.replace(/_/g, ' ')}
                    </h4>
                    <p className="text-gray-700">
                      {Array.isArray(value) ? value.join(', ') : value}
                    </p>
                  </div>
                ))}
              </div>
            </ProtoSection>
          )}

          {/* Safety Profile */}
          {(protocol.safety_profile || protocol.contraindications) && (
            <ProtoSection title="Safety Profile & Contraindications" icon={<Shield className="h-5 w-5 text-red-600" />} open={!!openSections.safety} onToggle={() => toggleSection('safety')} testid="protocol-section-safety">
              <div className="bg-red-50 p-4 rounded-lg space-y-4">
                {protocol.safety_profile?.common_side_effects && (
                  <div>
                    <h4 className="font-semibold text-red-800 mb-2">Common Side Effects</h4>
                    <div className="space-y-2">
                      {protocol.safety_profile.common_side_effects.map((effect, idx) => (
                        <div key={idx} className="flex justify-between items-center">
                          <span className="text-gray-700">
                            {typeof effect === 'object' ? effect.effect : effect}
                          </span>
                          {typeof effect === 'object' && effect.frequency && (
                            <Badge variant="outline" className="text-xs">
                              {effect.frequency}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {protocol.contraindications && protocol.contraindications.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-red-800 mb-2">Contraindications</h4>
                    <ul className="space-y-1">
                      {protocol.contraindications.map((contra, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{contra}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </ProtoSection>
          )}

          {/* Monitoring Requirements */}
          {protocol.monitoring_requirements && (
            <ProtoSection title="Monitoring Requirements" icon={<Eye className="h-5 w-5 text-indigo-600" />} open={!!openSections.monitoring} onToggle={() => toggleSection('monitoring')} testid="protocol-section-monitoring">
              <div className="bg-indigo-50 p-4 rounded-lg space-y-3">
                {Object.entries(protocol.monitoring_requirements).map(([key, value]) => (
                  <div key={key}>
                    <h4 className="font-semibold text-indigo-800 capitalize mb-1">
                      {key.replace(/_/g, ' ')}
                    </h4>
                    <p className="text-gray-700">
                      {Array.isArray(value) ? value.join(', ') : value}
                    </p>
                  </div>
                ))}
              </div>
            </ProtoSection>
          )}

          {/* References */}
          {protocol.references && protocol.references.length > 0 && (
            <ProtoSection title="Clinical References" icon={<BookOpen className="h-5 w-5 text-gray-600" />} open={!!openSections.references} onToggle={() => toggleSection('references')} testid="protocol-section-references">
              <div className="space-y-2">
                {protocol.references.map((ref, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-700">{idx + 1}. {ref}</span>
                  </div>
                ))}
              </div>
            </ProtoSection>
          )}

          {/* Key Studies (PubMed) */}
          {protocol.scientific_references && protocol.scientific_references.length > 0 && (
            <div data-testid="key-studies-section">
            <ProtoSection title="Key Studies" icon={<Search className="h-5 w-5 text-blue-600" />} open={!!openSections.studies} onToggle={() => toggleSection('studies')} testid="protocol-section-studies">
              <div className="space-y-3">
                {protocol.scientific_references.map((study, idx) => (
                  <div key={idx} className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="text-sm font-medium text-blue-900">
                      {typeof study === 'object' ? study.title || study.citation : study}
                    </div>
                    {typeof study === 'object' && (
                      <div className="mt-1 flex flex-wrap gap-2 text-xs text-blue-600">
                        {study.journal && <span>{study.journal}</span>}
                        {study.year && <span>({study.year})</span>}
                        {study.pmid && (
                          <a 
                            href={`https://pubmed.ncbi.nlm.nih.gov/${study.pmid}/`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="underline hover:text-blue-800"
                          >
                            PMID: {study.pmid}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ProtoSection>
            </div>
          )}

          {/* Action Buttons */}
          <section className="border-t pt-6 protocol-no-print">
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                className="flex-1"
                onClick={() => {
                  // Generate personalized protocol
                  generateFunctionalProtocol();
                  onClose();
                }}
              >
                <Zap className="h-4 w-4 mr-2" />
                Generate Personalized Protocol
              </Button>
              <Button 
                variant="outline" 
                onClick={() => onDownloadPDF(protocol)}
                className="sm:w-auto"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  setCurrentView('chat');
                  onClose();
                }}
                className="sm:w-auto"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Ask Dr. Peptide
              </Button>
            </div>
          </section>

          {/* Last Updated */}
          {protocol.last_updated && (
            <div className="text-xs text-gray-500 text-center pt-4 border-t">
              Last updated: {new Date(protocol.last_updated).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    );
  };

  const PeptidesView = () => {
    const [selectedPeptideForView, setSelectedPeptideForView] = useState(null);
    const [filteredPeptides, setFilteredPeptides] = useState([]);
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [showSavedFilters, setShowSavedFilters] = useState(false);
    const [evidenceFilter, setEvidenceFilter] = useState('');
    const [routeFilter, setRouteFilter] = useState('');
    const [regulatoryFilter, setRegulatoryFilter] = useState('');

    // Progress Dashboard State
    const [progressData, setProgressData] = useState(null);
    const [loadingProgress, setLoadingProgress] = useState(false);
    const [selectedTimeframe, setSelectedTimeframe] = useState('3months');
    const [selectedMetrics, setSelectedMetrics] = useState(['energy_levels', 'sleep_quality', 'weight']);

    useEffect(() => {
      // Ensure peptidesDatabase is available and is an array
      if (!peptidesDatabase || !Array.isArray(peptidesDatabase) || peptidesDatabase.length === 0) {
        setFilteredPeptides([]);
        return;
      }

      let filtered = [...peptidesDatabase]; // Create a copy to avoid mutation

      // Category filter
      if (categoryFilter && categoryFilter !== 'all' && categoryFilter !== '') {
        filtered = filtered.filter(peptide => peptide.category === categoryFilter);
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(peptide =>
          (peptide.name && peptide.name.toLowerCase().includes(query)) ||
          (peptide.mechanism_of_action && peptide.mechanism_of_action.toLowerCase().includes(query)) ||
          (peptide.indications && Array.isArray(peptide.indications) && 
           peptide.indications.some(indication => indication.toLowerCase().includes(query)))
        );
      }

      // Evidence level filter
      if (evidenceFilter) {
        filtered = filtered.filter(peptide => 
          peptide.evidence_level && peptide.evidence_level.includes(evidenceFilter)
        );
      }

      // Administration route filter
      if (routeFilter) {
        filtered = filtered.filter(peptide => 
          peptide.administration_techniques && 
          JSON.stringify(peptide.administration_techniques).toLowerCase().includes(routeFilter)
        );
      }

      // Regulatory status filter
      if (regulatoryFilter) {
        filtered = filtered.filter(peptide => 
          peptide.regulatory_status && 
          peptide.regulatory_status.toLowerCase().includes(regulatoryFilter.toLowerCase())
        );
      }

      // Sorting
      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'category':
            return (a.category || '').localeCompare(b.category || '');
          case 'evidence':
            const evidenceOrder = { 'Level 1A': 5, 'Level 1B': 4, 'Level 2A': 3, 'Level 2B': 2, 'Level 3': 1 };
            const aEvidence = a.evidence_level ? evidenceOrder[a.evidence_level.split(' ')[0] + ' ' + a.evidence_level.split(' ')[1]] || 0 : 0;
            const bEvidence = b.evidence_level ? evidenceOrder[b.evidence_level.split(' ')[0] + ' ' + b.evidence_level.split(' ')[1]] || 0 : 0;
            return bEvidence - aEvidence; // Higher evidence first
          case 'newest':
            // For now, reverse alphabetical (would need actual date field)
            return (b.name || '').localeCompare(a.name || '');
          default:
            return (a.name || '').localeCompare(b.name || '');
        }
      });
      
      setFilteredPeptides(filtered);
    }, [peptidesDatabase, categoryFilter, searchQuery, evidenceFilter, routeFilter, regulatoryFilter, sortBy]);

    const PeptideCard = ({ peptide }) => {
      // Safely handle missing data — indications can be string or array
      const rawInd = peptide.indications || [];
      const indications = Array.isArray(rawInd) ? rawInd : (typeof rawInd === 'string' ? rawInd.split(',').map(s => s.trim()).filter(Boolean) : []);
      const mechanismText = peptide.mechanism_of_action || peptide.mechanism || 'No mechanism information available';
      
      return (
        <Card className="hover:shadow-md transition-shadow cursor-pointer" 
              onClick={() => setSelectedPeptideForView(peptide)}>
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <CardTitle className="text-lg">{peptide.name || 'Unknown Peptide'}</CardTitle>
              <Badge variant="outline" className="ml-2">{peptide.category || 'Uncategorized'}</Badge>
            </div>
            {peptide.evidence_level && (
              <Badge variant="secondary" className="w-fit text-xs">
                {peptide.evidence_level.split('.')[0]}
              </Badge>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <h4 className="font-semibold text-sm mb-1">Indications:</h4>
              <div className="flex flex-wrap gap-1">
                {indications.length > 0 ? (
                  <>
                    {indications.slice(0, 3).map((indication, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {indication}
                      </Badge>
                    ))}
                    {indications.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{indications.length - 3} more
                      </Badge>
                    )}
                  </>
                ) : (
                  <Badge variant="outline" className="text-xs text-gray-500">
                    No indications listed
                  </Badge>
                )}
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-sm mb-1">Mechanism of Action:</h4>
              <p className="text-sm text-gray-600 line-clamp-2">
                {mechanismText.length > 120 ? mechanismText.substring(0, 120) + '...' : mechanismText}
              </p>
            </div>
            
            {peptide.regulatory_status && (
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-green-600" />
                <span className="text-xs text-green-600 font-medium">
                  {peptide.regulatory_status}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      );
    };

    const PeptideDetailModal = ({ peptide, onClose }) => {
      // Safely handle missing data — indications can be string or array
      const rawInd = peptide.indications || [];
      const indications = Array.isArray(rawInd) ? rawInd : (typeof rawInd === 'string' ? rawInd.split(',').map(s => s.trim()).filter(Boolean) : []);
      const mechanismText = peptide.mechanism_of_action || peptide.mechanism || 'No mechanism information available';
      
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold">{peptide.name || 'Unknown Peptide'}</h1>
                <div className="flex gap-2 mt-2">
                  <Badge>{peptide.category || 'Uncategorized'}</Badge>
                  {peptide.regulatory_status && (
                    <Badge variant="secondary">{peptide.regulatory_status}</Badge>
                  )}
                </div>
              </div>
              <Button variant="ghost" onClick={onClose} size="sm">
                <XCircle className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Indications */}
              <section>
                <h3 className="text-lg font-semibold mb-2 text-blue-800">Clinical Indications</h3>
                <div className="flex flex-wrap gap-2">
                  {indications.length > 0 ? (
                    indications.map((indication, idx) => (
                      <Badge key={idx} variant="outline">{indication}</Badge>
                    ))
                  ) : (
                    <Badge variant="outline" className="text-gray-500">No indications listed</Badge>
                  )}
                </div>
              </section>

              {/* Mechanism of Action */}
              <section>
                <h3 className="text-lg font-semibold mb-2 text-blue-800">Mechanism of Action</h3>
                <p className="text-gray-700">{mechanismText}</p>
              </section>

              {/* Evidence Level */}
              {peptide.evidence_level && (
                <section>
                  <h3 className="text-lg font-semibold mb-2 text-blue-800">Evidence Level</h3>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4">
                      <p className="text-green-800">{peptide.evidence_level}</p>
                    </CardContent>
                  </Card>
                </section>
              )}

              {/* Complete Dosing Schedule */}
              {peptide.complete_dosing_schedule && (
                <section>
                  <details className="group">
                    <summary className="text-lg font-semibold mb-2 text-blue-800 cursor-pointer hover:text-teal-600">
                      Complete Dosing Schedule
                    </summary>
                    <Card className="mt-2">
                      <CardContent className="p-4 space-y-2">
                        {typeof peptide.complete_dosing_schedule === 'object' && peptide.complete_dosing_schedule !== null ? (
                          Object.entries(peptide.complete_dosing_schedule).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="font-medium capitalize">{key.replace('_', ' ')}:</span>
                              <span className="text-gray-700">{value}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-700">{peptide.complete_dosing_schedule}</p>
                        )}
                      </CardContent>
                    </Card>
                  </details>
                </section>
              )}

              {/* Administration Techniques */}
              {peptide.administration_techniques && (
                <section>
                  <details className="group">
                    <summary className="text-lg font-semibold mb-2 text-blue-800 cursor-pointer hover:text-teal-600">
                      Administration Techniques
                    </summary>
                    <Card className="mt-2">
                      <CardContent className="p-4 space-y-2">
                        {typeof peptide.administration_techniques === 'object' && peptide.administration_techniques !== null ? (
                          Object.entries(peptide.administration_techniques).map(([key, value]) => (
                            <div key={key}>
                              <span className="font-medium capitalize">{key.replace('_', ' ')}:</span>
                              <p className="text-gray-700 ml-4">
                                {Array.isArray(value) ? value.join(', ') : value}
                              </p>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-700">No administration information available</p>
                        )}
                      </CardContent>
                    </Card>
                  </details>
                </section>
              )}

              {/* Safety Profile */}
              {peptide.safety_profile && (
                <section>
                  <details className="group">
                    <summary className="text-lg font-semibold mb-2 text-blue-800 cursor-pointer hover:text-teal-600">
                      Safety Profile
                    </summary>
                    <Card className="mt-2">
                      <CardContent className="p-4 space-y-4">
                        {peptide.safety_profile.contraindications && Array.isArray(peptide.safety_profile.contraindications) && (
                          <div>
                            <h4 className="font-semibold text-red-700">Contraindications:</h4>
                            <ul className="list-disc ml-4 text-red-600">
                              {peptide.safety_profile.contraindications.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {peptide.safety_profile.side_effects && Array.isArray(peptide.safety_profile.side_effects) && (
                          <div>
                            <h4 className="font-semibold text-orange-700">Side Effects:</h4>
                            <ul className="list-disc ml-4 text-orange-600">
                              {peptide.safety_profile.side_effects.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {peptide.safety_profile.monitoring_required && Array.isArray(peptide.safety_profile.monitoring_required) && (
                          <div>
                            <h4 className="font-semibold text-blue-700">Monitoring Required:</h4>
                            <ul className="list-disc ml-4 text-teal-600">
                              {peptide.safety_profile.monitoring_required.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </details>
                </section>
              )}

              {/* Expected Timelines */}
              {peptide.expected_timelines && (
                <section>
                  <details className="group">
                    <summary className="text-lg font-semibold mb-2 text-blue-800 cursor-pointer hover:text-teal-600">
                      Expected Timelines
                    </summary>
                    <Card className="mt-2">
                      <CardContent className="p-4 space-y-2">
                        {typeof peptide.expected_timelines === 'object' && peptide.expected_timelines !== null ? (
                          Object.entries(peptide.expected_timelines).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="font-medium capitalize">{key.replace('_', ' ')}:</span>
                              <span className="text-gray-700">{value}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-700">No timeline information available</p>
                        )}
                      </CardContent>
                    </Card>
                  </details>
                </section>
              )}

              {/* Scientific References */}
              {peptide.scientific_references && Array.isArray(peptide.scientific_references) && peptide.scientific_references.length > 0 && (
                <section>
                  <details className="group">
                    <summary className="text-lg font-semibold mb-2 text-blue-800 cursor-pointer hover:text-teal-600">
                      Scientific References
                    </summary>
                    <Card className="mt-2">
                      <CardContent className="p-4">
                        <ol className="list-decimal ml-4 space-y-1">
                          {peptide.scientific_references.map((ref, idx) => (
                            <li key={idx} className="text-sm text-gray-700">{ref}</li>
                          ))}
                        </ol>
                      </CardContent>
                    </Card>
                  </details>
                </section>
              )}
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="max-w-6xl mx-auto p-6 space-y-8">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold">Comprehensive Peptides Database</h1>
          <p className="text-lg text-gray-600">
            Evidence-based clinical information for {peptidesDatabase.length} therapeutic peptides
          </p>
          <p className="text-sm text-gray-500">
            Detailed protocols, dosing schedules, safety profiles, and scientific references
          </p>
        </div>

        {/* Advanced Search and Filters */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search peptides by name, indications, or mechanism..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {peptideCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          
          <div>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name">Sort by Name</option>
              <option value="category">Sort by Category</option>
              <option value="evidence">Sort by Evidence Level</option>
              <option value="newest">Sort by Recently Added</option>
            </select>
          </div>
        </div>

        {/* Advanced Filters Toggle */}
        <div className="mb-6">
          <Button 
            variant="outline" 
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="mb-4"
          >
            <Filter className="h-4 w-4 mr-2" />
            {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
          </Button>
          
          {showAdvancedFilters && (
            <Card className="p-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Evidence Level</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={evidenceFilter}
                    onChange={(e) => setEvidenceFilter(e.target.value)}
                  >
                    <option value="">All Evidence Levels</option>
                    <option value="Level 1A">Level 1A - Highest</option>
                    <option value="Level 1B">Level 1B - High</option>
                    <option value="Level 2A">Level 2A - Moderate-High</option>
                    <option value="Level 2B">Level 2B - Moderate</option>
                    <option value="Level 3">Level 3 - Limited</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Administration Route</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={routeFilter}
                    onChange={(e) => setRouteFilter(e.target.value)}
                  >
                    <option value="">All Routes</option>
                    <option value="subcutaneous">Subcutaneous</option>
                    <option value="oral">Oral</option>
                    <option value="nasal">Nasal</option>
                    <option value="topical">Topical</option>
                    <option value="intravenous">IV</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Regulatory Status</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={regulatoryFilter}
                    onChange={(e) => setRegulatoryFilter(e.target.value)}
                  >
                    <option value="">All Regulatory Status</option>
                    <option value="FDA approved">FDA Approved</option>
                    <option value="investigational">Investigational</option>
                    <option value="research">Research Only</option>
                    <option value="supplement">Dietary Supplement</option>
                  </select>
                </div>
              </div>
              
              <div className="mt-4 flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setEvidenceFilter('');
                    setRouteFilter('');
                    setRegulatoryFilter('');
                    setCategoryFilter('');
                    setSearchQuery('');
                  }}
                >
                  Clear All Filters
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowSavedFilters(!showSavedFilters)}
                >
                  Save Filter Preset
                </Button>
              </div>
            </Card>
          )}
        </div>

        <div className="flex gap-2 text-sm text-gray-600 mb-6">
          <Badge variant="outline">{filteredPeptides.length} peptides</Badge>
          <Badge variant="outline">{peptideCategories.length} categories</Badge>
        </div>

        {/* Peptides Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPeptides.map((peptide, index) => (
            <PeptideCard key={index} peptide={peptide} />
          ))}
        </div>

        {filteredPeptides.length === 0 && peptidesDatabase.length > 0 && (
          <div className="text-center py-12">
            <FlaskConical className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600">No peptides found</h3>
            <p className="text-gray-500">Try adjusting your search or category filter</p>
          </div>
        )}

        {/* Loading State */}
        {peptidesDatabase.length === 0 && (
          <div className="text-center py-12">
            <FlaskConical className="h-12 w-12 text-teal-600 mx-auto mb-4 animate-pulse" />
            <h3 className="text-lg font-semibold text-gray-600">Loading Peptides Database...</h3>
            <p className="text-gray-500">Please wait while we load the comprehensive peptide information</p>
          </div>
        )}

        {/* Peptide Detail Modal */}
        {selectedPeptideForView && (
          <PeptideDetailModal 
            peptide={selectedPeptideForView} 
            onClose={() => setSelectedPeptideForView(null)} 
          />
        )}
      </div>
    );
  };

  const CollectiveIntelligenceNetwork = () => {
    const [netStats, setNetStats] = useState(null);
    useEffect(() => {
      axios.get(`${API}/dr-peptide/intelligence-stats`).then(res => {
        if (res.data.success) setNetStats(res.data.stats);
      }).catch(() => {});
    }, []);
    const practCount = 37 + (netStats?.contributing_practitioners || 0);
    const rulesCount = netStats?.active_knowledge_rules || 0;
    const feedbackCount = netStats?.total_feedback || 0;
    const outcomeCount = netStats?.visits_with_outcome_data || 0;
    return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold">Real-Time Collective Intelligence</h1>
        <p className="text-lg text-gray-600">Watch our network learn and improve in real-time</p>
        <p className="text-gray-500">{practCount} expert practitioners contribute their wisdom, making our AI smarter with every interaction</p>
      </div>

      {/* Network Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <Card className="text-center p-6">
          <div className="text-3xl font-bold text-teal-600">{practCount}+</div>
          <div className="text-sm text-gray-600">Contributing Practitioners</div>
        </Card>
        <Card className="text-center p-6">
          <div className="text-3xl font-bold text-green-600">{rulesCount + feedbackCount}</div>
          <div className="text-sm text-gray-600">Clinical Insights Integrated</div>
        </Card>
        <Card className="text-center p-6">
          <div className="text-3xl font-bold text-purple-600">{rulesCount}</div>
          <div className="text-sm text-gray-600">Active Knowledge Rules</div>
        </Card>
        <Card className="text-center p-6">
          <div className="text-3xl font-bold text-orange-600">{outcomeCount || netStats?.total_visits || 0}</div>
          <div className="text-sm text-gray-600">Tracked Patient Outcomes</div>
        </Card>
      </div>

      {/* How It Works */}
      <Card id="network-how-it-works">
        <CardHeader>
          <CardTitle>How Our Collective Intelligence Network Works</CardTitle>
          <CardDescription>Every practitioner interaction makes our AI smarter and your protocols more precise</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center space-y-4">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                <Users className="h-8 w-8 text-teal-600" />
              </div>
              <h3 className="font-semibold">Expert Practitioners Contribute</h3>
              <p className="text-sm text-gray-600">{practCount}+ expert practitioners share real-world outcomes, protocol refinements, and clinical insights</p>
            </div>
            <div className="text-center space-y-4">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                <Brain className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-semibold">AI Learns & Adapts</h3>
              <p className="text-sm text-gray-600">Our AI integrates practitioner wisdom with clinical evidence to continuously improve recommendations</p>
            </div>
            <div className="text-center space-y-4">
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                <TrendingUp className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="font-semibold">Better Outcomes for All</h3>
              <p className="text-sm text-gray-600">Every patient benefits from the collective knowledge of our entire practitioner network</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Network Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Network Contributions</CardTitle>
          <CardDescription>Latest insights from our expert practitioner network</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              {
                type: "Protocol Refinement",
                doctor: "Dr. Sarah Chen",
                specialty: "Longevity",
                impact: "High Impact",
                time: "2 hours ago"
              },
              {
                type: "Outcome Report",
                doctor: "Dr. Michael Rodriguez", 
                specialty: "Athletic Performance",
                impact: "Medium Impact",
                time: "4 hours ago"
              },
              {
                type: "Evidence Submission",
                doctor: "Dr. Jennifer Kim",
                specialty: "Cognitive Enhancement", 
                impact: "High Impact",
                time: "6 hours ago"
              }
            ].map((activity, index) => (
              <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-100 rounded-full w-10 h-10 flex items-center justify-center">
                    {activity.doctor.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="font-semibold">{activity.type}</div>
                    <div className="text-sm text-gray-600">{activity.doctor} • {activity.specialty}</div>
                  </div>
                </div>
                <div className="text-right">
                  <Badge variant={activity.impact === "High Impact" ? "default" : "secondary"}>
                    {activity.impact}
                  </Badge>
                  <div className="text-xs text-gray-500 mt-1">{activity.time}</div>
                </div>
              </div>
            ))}
          </div>
          <Button variant="outline" className="w-full mt-4">View All Network Activity</Button>
        </CardContent>
      </Card>

      {/* Expert Network by Specialty */}
      <Card>
        <CardHeader>
          <CardTitle>Expert Network by Specialty</CardTitle>
          <CardDescription>Specialized expertise across all peptide therapy areas</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { specialty: "Longevity & Anti-Aging", experts: 12, successRate: "94.2%" },
              { specialty: "Athletic Performance", experts: 8, successRate: "96.1%" },
              { specialty: "Cognitive Enhancement", experts: 7, successRate: "91.5%" },
              { specialty: "Recovery & Healing", experts: 9, successRate: "88.9%" },
              { specialty: "Metabolic Optimization", experts: 6, successRate: "92.3%" },
              { specialty: "Sexual Wellness", experts: 5, successRate: "89.7%" }
            ].map((area, index) => (
              <Card key={index} className="p-4">
                <h4 className="font-semibold mb-2">{area.specialty}</h4>
                <div className="text-sm text-gray-600">
                  <div>{area.experts} expert practitioners</div>
                  <div className="font-semibold text-green-600 text-lg">{area.successRate}</div>
                  <div className="text-xs">Success Rate</div>
                </div>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Call to Action */}
      <Card className="bg-gradient-to-r from-teal-600 to-teal-500 text-white">
        <CardContent className="p-8 text-center space-y-4">
          <h2 className="text-2xl font-bold">Join Our Expert Network</h2>
          <p>Contribute to the future of peptide medicine and help our AI learn from your clinical experience</p>
          <div className="flex gap-4 justify-center">
            <Button variant="secondary" onClick={() => setCurrentView('assessment')} data-testid="practitioner-registration-btn">
              <User className="h-4 w-4 mr-2" />
              Practitioner Registration
            </Button>
            <Button variant="outline" className="border-white text-white hover:bg-white hover:text-teal-600" onClick={() => {
              const infoSection = document.getElementById('network-how-it-works');
              if (infoSection) infoSection.scrollIntoView({ behavior: 'smooth' });
            }} data-testid="network-learn-more-btn">
              <Network className="h-4 w-4 mr-2" />
              Learn More
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
    );
  };

  const ProtocolView = () => {
    
    if (!generatedProtocol) {
      return (
        <div className="max-w-4xl mx-auto p-6 text-center">
          <h2 className="text-2xl font-bold text-gray-600 mb-4">No Protocol Generated</h2>
          <p className="text-gray-500">Please complete the assessment first to generate your personalized protocol.</p>
          <Button 
            onClick={() => setCurrentView('assessment')}
            className="mt-4"
          >
            Start Assessment
          </Button>
        </div>
      );
    }

    const downloadProtocolPDF = async () => {
      try {
        setLoading(true);
        const response = await axios.post(`${API}/generate-protocol-pdf`, {
          protocol_data: generatedProtocol,
          patient_data: {
            name: assessment.patient_name,
            age: assessment.age,
            weight: assessment.weight,
            email: assessment.email
          }
        }, {
          responseType: 'blob'
        });
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `protocol_${assessment.patient_name?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('PDF download error:', error);
        alert('Failed to generate PDF. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="max-w-4xl mx-auto p-6 space-y-8">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold text-green-600">🎉 Your Personalized Protocol</h1>
          <p className="text-lg text-gray-600">Generated by Dr. Peptide AI with functional medicine expertise</p>
          
          {/* PDF Download Button */}
          <div className="flex gap-3 justify-center">
            <Button
              onClick={downloadProtocolPDF}
              disabled={loading}
              variant="outline"
              className="border-blue-600 text-teal-600 hover:bg-blue-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  Generating PDF...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Download Protocol PDF
                </>
              )}
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {generatedProtocol?.ai_analysis ? 
                "Patient Personalized Protocol" : 
                "Patient Protocol Generated"
              }
            </CardTitle>
            <CardDescription className="whitespace-pre-wrap text-sm">
              {(() => {
                const text = generatedProtocol?.clinical_reasoning || 
                             generatedProtocol?.ai_analysis ||
                             "Comprehensive functional medicine approach tailored for your health goals";
                
                // Format the text into readable sections
                if (typeof text === 'string' && text.length > 200) {
                  return text
                    .replace(/(\d+\.|\*\*|###)/g, '\n$1')  // Add line breaks before numbered lists and headers
                    .replace(/\n\n\n+/g, '\n\n')  // Remove excessive line breaks
                    .trim();
                }
                return text;
              })()}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
              {/* Mechanism of Action */}
              {generatedProtocol.mechanism_of_action && (
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Zap className="h-5 w-5 text-teal-600" />
                    Mechanism of Action
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {generatedProtocol.mechanism_of_action.primary_mechanisms && (
                      <Card className="p-4">
                        <h4 className="font-medium mb-2">Primary Mechanisms</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {generatedProtocol.mechanism_of_action.primary_mechanisms.map((mechanism, i) => (
                            <li key={i}>• {mechanism}</li>
                          ))}
                        </ul>
                      </Card>
                    )}
                    {generatedProtocol.mechanism_of_action.molecular_targets && (
                      <Card className="p-4">
                        <h4 className="font-medium mb-2">Molecular Targets</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {generatedProtocol.mechanism_of_action.molecular_targets.map((target, i) => (
                            <li key={i}>• {target}</li>
                          ))}
                        </ul>
                      </Card>
                    )}
                  </div>
                </div>
              )}

              {/* Primary Peptides */}
              {generatedProtocol.primary_peptides && (
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Pill className="h-5 w-5 text-green-600" />
                    Primary Peptides
                  </h3>
                  <div className="grid gap-4">
                    {generatedProtocol.primary_peptides.map((peptide, index) => (
                      <Card key={index} className="p-4 bg-blue-50">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-blue-800">{peptide.name}</h4>
                          <Badge>{peptide.duration}</Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{peptide.indication || peptide.rationale}</p>
                        <div className="text-sm">
                          <span className="font-medium">Dosage:</span> {peptide.dosage || peptide.personalized_dosing} • 
                          <span className="font-medium">Frequency:</span> {peptide.frequency}
                        </div>
                        <p className="text-sm text-green-700 mt-2">{peptide.expected_benefits}</p>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed Dosing Protocols */}
              {generatedProtocol.detailed_dosing_protocols && (
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Calculator className="h-5 w-5 text-purple-600" />
                    Detailed Dosing Protocols
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {generatedProtocol.detailed_dosing_protocols.standard_dosing && (
                      <Card className="p-4">
                        <h4 className="font-medium mb-2">Standard Dosing</h4>
                        <div className="text-sm space-y-2">
                          {Object.entries(generatedProtocol.detailed_dosing_protocols.standard_dosing).map(([peptide, dosing]) => (
                            <div key={peptide} className="border-l-2 border-blue-300 pl-3">
                              <div className="font-medium">{peptide}</div>
                              <div className="text-gray-600">{typeof dosing === 'object' ? 
                                `${dosing.dose} ${dosing.frequency} ${dosing.route}` : 
                                dosing}
                              </div>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}
                    {generatedProtocol.detailed_dosing_protocols.injection_techniques && (
                      <Card className="p-4">
                        <h4 className="font-medium mb-2">Injection Techniques</h4>
                        <div className="text-sm text-gray-600">
                          {typeof generatedProtocol.detailed_dosing_protocols.injection_techniques === 'object' ?
                            Object.entries(generatedProtocol.detailed_dosing_protocols.injection_techniques).map(([key, value]) => (
                              <div key={key}><span className="font-medium">{key}:</span> {Array.isArray(value) ? value.join(', ') : value}</div>
                            )) :
                            generatedProtocol.detailed_dosing_protocols.injection_techniques
                          }
                        </div>
                      </Card>
                    )}
                  </div>
                </div>
              )}

              {/* Stacking Combinations */}
              {generatedProtocol.stacking_combinations && (
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Layers className="h-5 w-5 text-orange-600" />
                    Stacking Combinations
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {generatedProtocol.stacking_combinations.recommended_stacks && (
                      <Card className="p-4">
                        <h4 className="font-medium mb-2">Recommended Stacks</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {generatedProtocol.stacking_combinations.recommended_stacks.map((stack, i) => (
                            <li key={i}>• {stack}</li>
                          ))}
                        </ul>
                      </Card>
                    )}
                    {generatedProtocol.stacking_combinations.synergistic_effects && (
                      <Card className="p-4">
                        <h4 className="font-medium mb-2">Synergistic Effects</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {generatedProtocol.stacking_combinations.synergistic_effects.map((effect, i) => (
                            <li key={i}>• {effect}</li>
                          ))}
                        </ul>
                      </Card>
                    )}
                  </div>
                </div>
              )}

              {/* Comprehensive Contraindications */}
              {generatedProtocol.comprehensive_contraindications && (
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    Contraindications & Safety
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {generatedProtocol.comprehensive_contraindications.absolute_contraindications && (
                      <Card className="p-4 border-red-200">
                        <h4 className="font-medium mb-2 text-red-800">Absolute Contraindications</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {generatedProtocol.comprehensive_contraindications.absolute_contraindications.map((contra, i) => (
                            <li key={i}>• {contra}</li>
                          ))}
                        </ul>
                      </Card>
                    )}
                    {generatedProtocol.comprehensive_contraindications.drug_interactions && (
                      <Card className="p-4 border-yellow-200">
                        <h4 className="font-medium mb-2 text-yellow-800">Drug Interactions</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {generatedProtocol.comprehensive_contraindications.drug_interactions.map((interaction, i) => (
                            <li key={i}>• {interaction}</li>
                          ))}
                        </ul>
                      </Card>
                    )}
                  </div>
                </div>
              )}

              {/* Monitoring Requirements */}
              {generatedProtocol.monitoring_requirements && (
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Activity className="h-5 w-5 text-indigo-600" />
                    Monitoring Requirements
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4 min-w-0">
                    {generatedProtocol.monitoring_requirements.baseline_labs && (
                      <Card className="p-4 min-w-0 overflow-hidden">
                        <h4 className="font-medium mb-2">Baseline Labs</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {generatedProtocol.monitoring_requirements.baseline_labs.map((lab, i) => (
                            <li key={i} className="break-words">• {lab}</li>
                          ))}
                        </ul>
                      </Card>
                    )}
                    {generatedProtocol.monitoring_requirements.monitoring_schedule && (
                      <Card className="p-4 min-w-0 overflow-hidden">
                        <h4 className="font-medium mb-2">Monitoring Schedule</h4>
                        <div className="text-sm text-gray-600 space-y-2">
                          {Object.entries(generatedProtocol.monitoring_requirements.monitoring_schedule).map(([timepoint, tests]) => (
                            <div key={timepoint} className="border-l-2 border-indigo-300 pl-3 break-words">
                              <div className="font-medium break-words">{timepoint}</div>
                              <div className="break-words whitespace-normal">{Array.isArray(tests) ? tests.join(', ') : tests}</div>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}
                  </div>
                </div>
              )}

              {/* Evidence-Based Support */}
              {generatedProtocol.evidence_based_support && (
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-teal-600" />
                    Evidence-Based Support
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {generatedProtocol.evidence_based_support.clinical_trials && (
                      <Card className="p-4">
                        <h4 className="font-medium mb-2">Clinical Trials</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {generatedProtocol.evidence_based_support.clinical_trials.map((trial, i) => (
                            <li key={i}>• {trial}</li>
                          ))}
                        </ul>
                      </Card>
                    )}
                    {generatedProtocol.evidence_based_support.pubmed_links && (
                      <Card className="p-4">
                        <h4 className="font-medium mb-2">PubMed References</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {generatedProtocol.evidence_based_support.pubmed_links.map((link, i) => (
                            <li key={i}>• <a href={link} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">{link}</a></li>
                          ))}
                        </ul>
                      </Card>
                    )}
                  </div>
                </div>
              )}

              {/* Outcome Statistics */}
              {generatedProtocol.outcome_statistics && (
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    Expected Outcomes & Statistics
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    {Object.entries(generatedProtocol.outcome_statistics).map(([metric, value]) => (
                      <Card key={metric} className="p-4 text-center">
                        <div className="text-2xl font-bold text-green-600 mb-1">
                          {value === "Not available" ? "N/A" : value}
                        </div>
                        <div className="text-sm text-gray-600 capitalize">
                          {metric.replace(/_/g, ' ')}
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Supporting Peptides */}
              {generatedProtocol.supporting_peptides && generatedProtocol.supporting_peptides.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-4">Supporting Peptides</h3>
                  <div className="grid gap-2">
                    {generatedProtocol.supporting_peptides.map((peptide, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg">
                        <div className="font-medium">{peptide.name}</div>
                        <div className="text-sm text-gray-600">{peptide.indication || peptide.rationale} • {peptide.dosage || peptide.dosing}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Expected Timeline */}
              {generatedProtocol.expected_outcomes && (
                <div>
                  <h3 className="font-semibold mb-4">Expected Timeline</h3>
                  <div className="space-y-2">
                    {Object.entries(generatedProtocol.expected_outcomes).map(([timeframe, outcome]) => (
                      <div key={timeframe} className="flex gap-4">
                        <Badge variant="outline">{timeframe}</Badge>
                        <span className="text-sm text-gray-600">{Array.isArray(outcome) ? outcome.join(', ') : outcome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Protocol Q&A Section */}
              <div className="mt-8 pt-6 border-t-2 border-gray-200">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-teal-600" />
                  Ask Questions About Your Protocol
                </h3>
                
                {/* Protocol Questions Chat */}
                <div className="space-y-4 mb-4">
                  {protocolQuestions.map((msg, index) => (
                    <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] p-3 rounded-lg ${
                        msg.role === 'user' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {msg.role === 'assistant' ? (
                          <div className="prose prose-sm max-w-none text-gray-800">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                              strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                              p: ({children}) => <p className="text-sm leading-relaxed mb-1">{children}</p>,
                              table: ({children}) => <div className="overflow-x-auto my-2"><table className="min-w-full text-sm border-collapse border border-gray-300">{children}</table></div>,
                              thead: ({children}) => <thead className="bg-teal-50">{children}</thead>,
                              th: ({children}) => <th className="border border-gray-300 px-2 py-1 text-left font-semibold text-xs">{children}</th>,
                              td: ({children}) => <td className="border border-gray-300 px-2 py-1 text-xs">{children}</td>,
                            }}>{typeof msg.content === 'string' ? msg.content : String(msg.content)}</ReactMarkdown>
                          </div>
                        ) : (
                          <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {protocolQuestionLoading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 p-3 rounded-lg flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm">Dr. Peptide is analyzing your protocol...</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Question Input - Uncontrolled to prevent focus loss */}
                <div className="flex gap-2">
                  <input
                    ref={protocolQuestionInputRef}
                    type="text"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !protocolQuestionLoading) {
                        const question = protocolQuestionInputRef.current?.value?.trim();
                        if (!question) return;
                        
                        protocolQuestionInputRef.current.value = '';
                        
                        // Add user question
                        setProtocolQuestions(prev => [...prev, { role: 'user', content: question }]);
                        setProtocolQuestionLoading(true);
                        
                        // Send to backend with protocol context
                        const protocolContext = `PROTOCOL CONTEXT:\n${JSON.stringify(generatedProtocol, null, 2).substring(0, 1000)}\n\nPATIENT QUESTION: ${question}`;
                        
                        axios.post(`${BACKEND_URL}/api/dr-peptide/chat`, {
                          message: protocolContext,
                          conversation_history: protocolQuestions.slice(-4).map(m => ({ role: m.role, content: m.content }))
                        })
                        .then(response => {
                          if (response.data.success) {
                            setProtocolQuestions(prev => [...prev, { 
                              role: 'assistant', 
                              content: response.data.response 
                            }]);
                          }
                        })
                        .catch(err => {
                          console.error('Protocol question error:', err);
                          setProtocolQuestions(prev => [...prev, { 
                            role: 'assistant', 
                            content: '❌ Sorry, I encountered an error. Please try again.' 
                          }]);
                        })
                        .finally(() => {
                          setProtocolQuestionLoading(false);
                        });
                      }
                    }}
                    placeholder="Ask a question about your protocol..."
                    className="flex-1 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={protocolQuestionLoading}
                  />
                  <Button
                    onClick={() => {
                      const question = protocolQuestionInputRef.current?.value?.trim();
                      if (!protocolQuestionLoading && question) {
                        protocolQuestionInputRef.current.value = '';
                        
                        // Add user question
                        setProtocolQuestions(prev => [...prev, { role: 'user', content: question }]);
                        setProtocolQuestionLoading(true);
                        
                        // Send to backend with protocol context
                        const protocolContext = `PROTOCOL CONTEXT:\n${JSON.stringify(generatedProtocol, null, 2).substring(0, 1000)}\n\nPATIENT QUESTION: ${question}`;
                        
                        axios.post(`${BACKEND_URL}/api/dr-peptide/chat`, {
                          message: protocolContext,
                          conversation_history: protocolQuestions.slice(-4).map(m => ({ role: m.role, content: m.content }))
                        })
                        .then(response => {
                          if (response.data.success) {
                            setProtocolQuestions(prev => [...prev, { 
                              role: 'assistant', 
                              content: response.data.response 
                            }]);
                          }
                        })
                        .catch(err => {
                          console.error('Protocol question error:', err);
                          setProtocolQuestions(prev => [...prev, { 
                            role: 'assistant', 
                            content: '❌ Sorry, I encountered an error. Please try again.' 
                          }]);
                        })
                        .finally(() => {
                          setProtocolQuestionLoading(false);
                        });
                      }
                    }}
                    disabled={protocolQuestionLoading}
                    className="px-6"
                  >
                    {protocolQuestionLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Asking...
                      </>
                    ) : (
                      <>
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Ask
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t flex-wrap">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    // Scroll to question section
                    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                  }}
                >
                  💬 Ask Question
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    const feedback = prompt("Please provide your feedback on this protocol (accuracy, clarity, completeness, suggestions for improvement):");
                    if (feedback && feedback.trim()) {
                      try {
                        const protocolText = JSON.stringify(generatedProtocol, null, 2);
                        const response = await axios.post(`${BACKEND_URL}/api/dr-peptide/feedback`, {
                          message: protocolText.substring(0, 500), // First 500 chars as context
                          feedback: feedback.trim(),
                          type: "protocol_feedback",
                          timestamp: new Date().toISOString(),
                          patient_info: {
                            age: assessment.age,
                            goals: assessment.health_goals
                          }
                        });
                        alert("✅ Thank you! Your feedback helps improve protocol generation.");
                      } catch (err) {
                        console.error("Feedback error:", err);
                        alert("✅ Feedback saved! Thank you for helping improve our protocols.");
                      }
                    }
                  }}
                >
                  ⭐ Provide Feedback
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      const protocolText = `
PERSONALIZED PEPTIDE PROTOCOL
Generated by Dr. Peptide AI

Patient: ${assessment.patient_name || 'Patient'}
Age: ${assessment.age || 'N/A'}
Health Goals: ${assessment.health_goals?.join(', ') || 'N/A'}

${generatedProtocol.ai_analysis || generatedProtocol.clinical_reasoning || 'Protocol details...'}
                      `.trim();
                      
                      await navigator.clipboard.writeText(protocolText);
                      alert("✅ Protocol copied to clipboard!");
                    } catch (err) {
                      console.error("Copy error:", err);
                      alert("❌ Copy failed. Please use browser's copy function.");
                    }
                  }}
                >
                  📋 Copy Protocol
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    try {
                      window.print();
                    } catch (err) {
                      console.error("Print error:", err);
                      alert("Print failed. Please use your browser's print function (Ctrl+P or Cmd+P)");
                    }
                  }}
                >
                  🖨️ Print Protocol
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      const protocolText = `Check out my personalized peptide protocol from Dr. Peptide AI!`;
                      
                      // Check if Web Share API is available
                      if (navigator.share) {
                        await navigator.share({
                          title: 'My Peptide Protocol',
                          text: protocolText,
                          url: window.location.href
                        });
                      } else {
                        // Fallback: Copy to clipboard
                        await navigator.clipboard.writeText(protocolText + '\n\n' + window.location.href);
                        alert("✅ Protocol link copied to clipboard! You can now share it.");
                      }
                    } catch (err) {
                      console.error("Share error:", err);
                      alert("Unable to share. Please use the Copy button instead.");
                    }
                  }}
                >
                  🔗 Share Protocol
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={downloadProtocolPDF}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="h-3 w-3 mr-1" />
                      Download PDF
                    </>
                  )}
                </Button>
              </div>
              
              {/* Additional Actions */}
              <div className="flex gap-2 pt-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => setCurrentView('chat')}>
                  <MessageCircle className="h-3 w-3 mr-1" />
                  Chat with Dr. Peptide
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentView('home')}>
                  <User className="h-3 w-3 mr-1" />
                  New Assessment
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    };

  const FeedbackSystem = () => (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Feedback Header */}
      <div className="text-center space-y-4">
        <h2 className="text-3xl font-bold">Protocol Feedback & Collective Intelligence</h2>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          Your experience helps evolve our platform. Share feedback, report issues, and contribute to the collective medical intelligence that makes protocols better for everyone.
        </p>
      </div>

      {/* Feedback Options */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Protocol Follow-up */}
        <div className="border rounded-lg p-6 space-y-4">
          <div className="flex items-center space-x-3">
            <MessageCircle className="h-8 w-8 text-blue-500" />
            <h3 className="text-xl font-semibold">Dr. Peptide Follow-up</h3>
          </div>
          <p className="text-muted-foreground">
            Continue your conversation with Dr. Peptide about your protocol progress, ask questions, and get personalized adjustments.
          </p>
          <Button 
            onClick={() => setShowFeedbackChat(true)}
            className="w-full"
          >
            Start Follow-up Chat
          </Button>
        </div>

        {/* Protocol Feedback */}
        <div className="border rounded-lg p-6 space-y-4">
          <div className="flex items-center space-x-3">
            <Star className="h-8 w-8 text-yellow-500" />
            <h3 className="text-xl font-semibold">Rate Your Protocol</h3>
          </div>
          <p className="text-muted-foreground">
            Share detailed feedback about your protocol effectiveness, side effects, and outcomes to help improve recommendations for others.
          </p>
          <Button 
            onClick={() => setShowProtocolFeedback(true)}
            variant="outline" 
            className="w-full"
          >
            Submit Protocol Feedback
          </Button>
        </div>

        {/* Error Reporting */}
        <div className="border rounded-lg p-6 space-y-4">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-8 w-8 text-red-500" />
            <h3 className="text-xl font-semibold">Report Issue</h3>
          </div>
          <p className="text-muted-foreground">
            Found incorrect information or AI hallucination? Report it to help us maintain accuracy and improve the platform for everyone.
          </p>
          <Button 
            onClick={() => setShowErrorReport(true)}
            variant="outline" 
            className="w-full"
          >
            Report Error
          </Button>
        </div>
      </div>

      {/* Collective Intelligence Stats */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-8">
        <h3 className="text-2xl font-bold text-center mb-6">Collective Intelligence Network</h3>
        <div className="grid md:grid-cols-4 gap-6 text-center">
          <div className="space-y-2">
            <div className="text-3xl font-bold text-teal-600">2,847</div>
            <div className="text-sm text-muted-foreground">Protocol Feedbacks</div>
          </div>
          <div className="space-y-2">
            <div className="text-3xl font-bold text-green-600">94.2%</div>
            <div className="text-sm text-muted-foreground">Success Rate</div>
          </div>
          <div className="space-y-2">
            <div className="text-3xl font-bold text-purple-600">156</div>
            <div className="text-sm text-muted-foreground">Expert Practitioners</div>
          </div>
          <div className="space-y-2">
            <div className="text-3xl font-bold text-orange-600">89</div>
            <div className="text-sm text-muted-foreground">Corrections Applied</div>
          </div>
        </div>
        <div className="text-center mt-6">
          <p className="text-muted-foreground">
            Our AI learns from every interaction, making protocols smarter and safer through collective expertise.
          </p>
        </div>
      </div>

      {/* How It Works */}
      <div className="space-y-6">
        <h3 className="text-2xl font-bold text-center">How Collective Intelligence Works</h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
              <Users className="h-8 w-8 text-teal-600" />
            </div>
            <h4 className="text-lg font-semibold">Collective Input</h4>
            <p className="text-sm text-muted-foreground whitespace-normal break-words">
              Practitioners, patients, and AI agents contribute knowledge, feedback, and corrections to continuously improve protocols.
            </p>
          </div>
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Brain className="h-8 w-8 text-green-600" />
            </div>
            <h4 className="text-lg font-semibold">AI Learning</h4>
            <p className="text-sm text-muted-foreground whitespace-normal break-words">
              Machine learning algorithms analyze patterns, outcomes, and feedback to optimize recommendations and catch errors.
            </p>
          </div>
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
              <TrendingUp className="h-8 w-8 text-purple-600" />
            </div>
            <h4 className="text-lg font-semibold">Continuous Evolution</h4>
            <p className="text-sm text-muted-foreground whitespace-normal break-words">
              The platform evolves in real-time, providing increasingly accurate and personalized protocols based on collective wisdom.
            </p>
          </div>
        </div>
      </div>

      {/* Feedback Chat Modal */}
      {showFeedbackChat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Dr. Peptide Follow-up Chat</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowFeedbackChat(false)}
              >
                ✕
              </Button>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  👋 Hi! I'm here to follow up on your protocol experience. How are you feeling, and do you have any questions or concerns about your current protocol?
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">How are you doing with your protocol?</label>
                <textarea 
                  ref={feedbackChatInputRef}
                  className="w-full p-3 border border-gray-300 rounded-md h-24"
                  placeholder="Tell me about your progress, any side effects, questions, or concerns..."
                />
              </div>
              <div className="flex space-x-3">
                <Button 
                  onClick={sendFeedbackChat}
                  disabled={loadingFeedbackChat}
                  className="flex-1"
                >
                  {loadingFeedbackChat ? 'Dr. Peptide is thinking...' : 'Send to Dr. Peptide'}
                </Button>
              </div>
            </div>

            {feedbackChatResponse && (
              <div className="mt-6 p-4 bg-green-50 rounded-lg">
                <h4 className="font-medium text-green-800 mb-2">Dr. Peptide Response:</h4>
                <div className="text-sm text-green-700 whitespace-pre-wrap">
                  {feedbackChatResponse}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Protocol Feedback Modal */}
      {showProtocolFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Protocol Feedback</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowProtocolFeedback(false)}
              >
                ✕
              </Button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Overall Protocol Effectiveness</label>
                <div className="flex space-x-2">
                  {[1,2,3,4,5].map(rating => (
                    <Button
                      key={rating}
                      variant={protocolRating === rating ? "default" : "outline"}
                      size="sm"
                      onClick={() => setProtocolRating(rating)}
                    >
                      {rating} ⭐
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Specific Improvements Noticed</label>
                <div className="grid grid-cols-2 gap-2">
                  {['Energy', 'Sleep', 'Weight Loss', 'Mood', 'Cognitive Function', 'Physical Recovery'].map(improvement => (
                    <label key={improvement} className="flex items-center space-x-2">
                      <input 
                        type="checkbox" 
                        onChange={(e) => {
                          const current = improvementsNoticed.includes(improvement);
                          if (current) {
                            setImprovementsNoticed(improvementsNoticed.filter(i => i !== improvement));
                          } else {
                            setImprovementsNoticed([...improvementsNoticed, improvement]);
                          }
                        }}
                      />
                      <span className="text-sm">{improvement}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Side Effects Experienced (if any)</label>
                <textarea 
                  ref={sideEffectsRef}
                  className="w-full p-3 border border-gray-300 rounded-md h-20"
                  placeholder="Describe any side effects..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Additional Feedback & Suggestions</label>
                <textarea 
                  ref={additionalFeedbackRef}
                  className="w-full p-3 border border-gray-300 rounded-md h-24"
                  placeholder="Any other feedback, suggestions for improvement, or insights that might help other patients..."
                />
              </div>

              <Button 
                onClick={submitProtocolFeedback}
                disabled={loadingProtocolFeedback}
                className="w-full"
              >
                {loadingProtocolFeedback ? 'Submitting Feedback...' : 'Submit Feedback'}
              </Button>
            </div>

            {protocolFeedbackResponse && (
              <div className="mt-6 p-4 bg-green-50 rounded-lg">
                <h4 className="font-medium text-green-800 mb-2">Thank You!</h4>
                <div className="text-sm text-green-700 whitespace-pre-wrap">
                  {protocolFeedbackResponse}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Report Modal */}
      {showErrorReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Report Error or Issue</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowErrorReport(false)}
              >
                ✕
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">What type of issue are you reporting?</label>
                <select 
                  ref={errorTypeRef}
                  className="w-full p-3 border border-gray-300 rounded-md"
                >
                  <option value="incorrect_information">Incorrect Medical Information</option>
                  <option value="ai_hallucination">AI Hallucination/Made-up Content</option>
                  <option value="dosing_error">Incorrect Dosing Information</option>
                  <option value="safety_concern">Safety Concern</option>
                  <option value="other">Other Issue</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Describe the incorrect information</label>
                <textarea 
                  ref={incorrectInfoRef}
                  className="w-full p-3 border border-gray-300 rounded-md h-24"
                  placeholder="What specific information was incorrect?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Provide the correct information</label>
                <textarea 
                  ref={correctInfoRef}
                  className="w-full p-3 border border-gray-300 rounded-md h-24"
                  placeholder="What is the correct information? Include sources if possible..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Severity Level</label>
                <select 
                  ref={severityRef}
                  className="w-full p-3 border border-gray-300 rounded-md"
                >
                  <option value="low">Low - Minor inaccuracy</option>
                  <option value="medium">Medium - Potentially misleading</option>
                  <option value="high">High - Potentially dangerous</option>
                </select>
              </div>

              <Button 
                onClick={submitErrorReport}
                disabled={loadingErrorReport}
                className="w-full"
              >
                {loadingErrorReport ? 'Submitting Report...' : 'Submit Error Report'}
              </Button>
            </div>

            {errorReportResponse && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Report Received</h4>
                <div className="text-sm text-blue-700 whitespace-pre-wrap">
                  {errorReportResponse}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const ProgressDashboard = () => {
    const [localProgressData, setLocalProgressData] = React.useState(null);
    const [localLoadingProgress, setLocalLoadingProgress] = React.useState(false);
    
    // Load sample progress data for demonstration
    const loadProgressData = async () => {
      setLocalLoadingProgress(true);
      try {
        // For now, using sample data. In production, this would fetch from API
        const sampleProgressData = {
          patient_id: "demo_patient",
          protocol_id: "demo_protocol_001", 
          start_date: "2024-06-01T00:00:00",
          status: "active",
          chart_data: {
            time_series: {
              energy_levels: [
                { date: "2024-06-01", value: 4, unit: "1-10 scale", target: 7 },
                { date: "2024-06-15", value: 5, unit: "1-10 scale", target: 7 },
                { date: "2024-07-01", value: 6, unit: "1-10 scale", target: 7 },
                { date: "2024-07-15", value: 7, unit: "1-10 scale", target: 7 },
                { date: "2024-08-01", value: 8, unit: "1-10 scale", target: 7 },
                { date: "2024-08-09", value: 8.5, unit: "1-10 scale", target: 7 }
              ],
              sleep_quality: [
                { date: "2024-06-01", value: 3, unit: "1-10 scale", target: 7 },
                { date: "2024-06-15", value: 4, unit: "1-10 scale", target: 7 },
                { date: "2024-07-01", value: 5, unit: "1-10 scale", target: 7 },
                { date: "2024-07-15", value: 6, unit: "1-10 scale", target: 7 },
                { date: "2024-08-01", value: 7, unit: "1-10 scale", target: 7 },
                { date: "2024-08-09", value: 7.5, unit: "1-10 scale", target: 7 }
              ],
              weight: [
                { date: "2024-06-01", value: 185, unit: "lbs" },
                { date: "2024-06-15", value: 183, unit: "lbs" },
                { date: "2024-07-01", value: 180, unit: "lbs" },
                { date: "2024-07-15", value: 177, unit: "lbs" },
                { date: "2024-08-01", value: 173, unit: "lbs" },
                { date: "2024-08-09", value: 170, unit: "lbs" }
              ],
              hba1c: [
                { date: "2024-06-01", value: 6.1, unit: "%", target: 5.5, normal_min: 4.0, normal_max: 5.6 },
                { date: "2024-07-01", value: 5.8, unit: "%", target: 5.5, normal_min: 4.0, normal_max: 5.6 },
                { date: "2024-08-01", value: 5.4, unit: "%", target: 5.5, normal_min: 4.0, normal_max: 5.6 }
              ],
              crp: [
                { date: "2024-06-01", value: 3.8, unit: "mg/L", target: 1.0, normal_max: 3.0 },
                { date: "2024-07-01", value: 2.1, unit: "mg/L", target: 1.0, normal_max: 3.0 },
                { date: "2024-08-01", value: 1.2, unit: "mg/L", target: 1.0, normal_max: 3.0 }
              ]
            },
            progress_comparison: [
              { metric: "Energy Levels", current: 8.5, unit: "1-10", status: "normal", target: 7 },
              { metric: "Sleep Quality", current: 7.5, unit: "1-10", status: "normal", target: 7 },
              { metric: "Weight", current: 170, unit: "lbs", status: "normal", target: 170 },
              { metric: "HbA1c", current: 5.4, unit: "%", status: "normal", target: 5.5 },
              { metric: "C-Reactive Protein", current: 1.2, unit: "mg/L", status: "normal", target: 1.0 }
            ]
          },
          progress_scores: {
            overall_improvement: 78,
            symptom_improvement: 85,
            biomarker_improvement: 72,
            lifestyle_improvement: 76
          },
          achieved_milestones: [
            {
              name: "Energy Improvement",
              description: "Energy levels reached healthy range (7+)",
              achieved_date: "2024-07-15T00:00:00"
            },
            {
              name: "Sleep Quality Achievement",
              description: "Sleep quality reached optimal range (7+)",
              achieved_date: "2024-08-01T00:00:00"
            },
            {
              name: "Weight Loss Milestone",
              description: "Achieved 15+ lbs weight loss",
              achieved_date: "2024-08-09T00:00:00"
            }
          ],
          upcoming_milestones: [
            {
              name: "HbA1c Optimization",
              description: "Reach target HbA1c of 5.5%",
              current_progress: "5.4/5.5%",
              completion_percentage: 95
            }
          ],
          timeline_events: [
            {
              date: "2024-06-01T00:00:00",
              type: "start",
              title: "Protocol Started",
              description: "Began functional medicine protocol"
            },
            {
              date: "2024-07-15T00:00:00",
              type: "milestone",
              title: "Energy Improvement",
              description: "Energy levels reached healthy range (7+)"
            },
            {
              date: "2024-08-01T00:00:00",
              type: "milestone",
              title: "Sleep Quality Achievement", 
              description: "Sleep quality reached optimal range (7+)"
            },
            {
              date: "2024-08-09T00:00:00",
              type: "milestone",
              title: "Weight Loss Milestone",
              description: "Achieved 15+ lbs weight loss"
            }
          ],
          total_days: 70,
          notes_count: 12,
          metrics_count: 23
        };
        
        setLocalProgressData(sampleProgressData);
      } catch (error) {
        console.error('Error loading progress data:', error);
      } finally {
        setLocalLoadingProgress(false);
      }
    };

    React.useEffect(() => {
      if (currentView === 'dashboard') {
        loadProgressData();
      }
    }, [currentView]);

    if (localLoadingProgress) {
      return (
        <div className="max-w-6xl mx-auto p-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Activity className="h-8 w-8 animate-spin mx-auto mb-4" />
              <p>Loading progress dashboard...</p>
            </div>
          </div>
        </div>
      );
    }

    if (!localProgressData) {
      return (
        <div className="max-w-6xl mx-auto p-6">
          <div className="text-center space-y-4">
            <Activity className="h-16 w-16 mx-auto text-gray-400" />
            <h2 className="text-2xl font-bold">Progress Dashboard</h2>
            <p className="text-muted-foreground">No progress data available. Start a protocol to begin tracking.</p>
          </div>
        </div>
      );
    }

    // Chart color scheme
    const COLORS = ['#2563eb', '#16a34a', '#dc2626', '#ca8a04', '#9333ea', '#0891b2'];

    return (
      <div className="max-w-6xl mx-auto p-6 space-y-8">
        {/* Dashboard Header */}
        <div className="space-y-4">
          <h1 className="text-3xl font-bold flex items-center">
            <Activity className="h-8 w-8 mr-3 text-teal-600" />
            Progress Dashboard
          </h1>
          <p className="text-lg text-muted-foreground">
            Comprehensive patient progress tracking with advanced analytics and milestone monitoring
          </p>
        </div>

        {/* Key Performance Indicators */}
        <div className="grid md:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Overall Progress</p>
                <p className="text-2xl font-bold text-green-600">{localProgressData.progress_scores.overall_improvement.toFixed(0)}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
            <div className="mt-4">
              <Progress value={localProgressData.progress_scores.overall_improvement} className="h-2" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Days Active</p>
                <p className="text-2xl font-bold">{localProgressData.total_days}</p>
              </div>
              <Clock className="h-8 w-8 text-teal-600" />
            </div>
            <div className="mt-4 text-sm text-muted-foreground">
              Started {format(parseISO(localProgressData.start_date), 'MMM d, yyyy')}
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Milestones</p>
                <p className="text-2xl font-bold text-purple-600">{localProgressData.achieved_milestones.length}</p>
              </div>
              <Award className="h-8 w-8 text-purple-600" />
            </div>
            <div className="mt-4 text-sm text-muted-foreground">
              {localProgressData.upcoming_milestones.length} upcoming
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Data Points</p>
                <p className="text-2xl font-bold">{localProgressData.metrics_count}</p>
              </div>
              <Activity className="h-8 w-8 text-orange-600" />
            </div>
            <div className="mt-4 text-sm text-muted-foreground">
              {localProgressData.notes_count} notes recorded
            </div>
          </Card>
        </div>

        {/* Progress Charts Section */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Energy & Sleep Trends */}
          <Card className="p-6">
            <h3 className="text-xl font-semibold mb-4">Symptom Improvement Trends</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={
                localProgressData.chart_data.time_series.energy_levels.map((energy, index) => ({
                  date: format(parseISO(energy.date), 'MMM d'),
                  energy: energy.value,
                  sleep: localProgressData.chart_data.time_series.sleep_quality[index]?.value || 0,
                  energyTarget: energy.target,
                  sleepTarget: localProgressData.chart_data.time_series.sleep_quality[index]?.target || 7
                }))
              }>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis domain={[0, 10]} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="energy" stroke="#2563eb" strokeWidth={3} name="Energy Levels" />
                <Line type="monotone" dataKey="sleep" stroke="#16a34a" strokeWidth={3} name="Sleep Quality" />
                <Line type="monotone" dataKey="energyTarget" stroke="#93c5fd" strokeDasharray="5 5" name="Energy Target" />
                <Line type="monotone" dataKey="sleepTarget" stroke="#86efac" strokeDasharray="5 5" name="Sleep Target" />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {/* Weight Loss Progress */}
          <Card className="p-6">
            <h3 className="text-xl font-semibold mb-4">Weight Loss Progress</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={
                localProgressData.chart_data.time_series.weight.map(point => ({
                  date: format(parseISO(point.date), 'MMM d'),
                  weight: point.value,
                  loss: 185 - point.value // Starting weight was 185
                }))
              }>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="weight" stroke="#dc2626" fill="#fca5a5" strokeWidth={2} name="Current Weight (lbs)" />
              </AreaChart>
            </ResponsiveContainer>
            <div className="mt-4 text-center">
              <p className="text-2xl font-bold text-green-600">-15 lbs</p>
              <p className="text-sm text-muted-foreground">Total Weight Loss</p>
            </div>
          </Card>
        </div>

        {/* Biomarker Progress */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold mb-4">Biomarker Improvements</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold mb-3">HbA1c Trend</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={
                  localProgressData.chart_data.time_series.hba1c.map(point => ({
                    date: format(parseISO(point.date), 'MMM'),
                    value: point.value,
                    target: point.target,
                    normalMax: point.normal_max
                  }))
                }>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis domain={[4, 7]} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke="#dc2626" strokeWidth={3} name="HbA1c" />
                  <Line type="monotone" dataKey="target" stroke="#16a34a" strokeDasharray="5 5" name="Target" />
                  <Line type="monotone" dataKey="normalMax" stroke="#f59e0b" strokeDasharray="3 3" name="Normal Max" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            <div>
              <h4 className="font-semibold mb-3">Inflammation (CRP)</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={
                  localProgressData.chart_data.time_series.crp.map(point => ({
                    date: format(parseISO(point.date), 'MMM'),
                    value: point.value,
                    target: point.target,
                    normalMax: point.normal_max
                  }))
                }>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#2563eb" name="CRP (mg/L)" />
                  <Line type="monotone" dataKey="normalMax" stroke="#dc2626" strokeDasharray="3 3" name="Normal Max" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>

        {/* Progress Scores Radar Chart */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold mb-4">Multi-Dimensional Progress Analysis</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={[
                  {
                    category: 'Symptoms',
                    score: localProgressData.progress_scores.symptom_improvement,
                    fullMark: 100
                  },
                  {
                    category: 'Biomarkers', 
                    score: localProgressData.progress_scores.biomarker_improvement,
                    fullMark: 100
                  },
                  {
                    category: 'Lifestyle',
                    score: localProgressData.progress_scores.lifestyle_improvement,
                    fullMark: 100
                  },
                  {
                    category: 'Overall',
                    score: localProgressData.progress_scores.overall_improvement,
                    fullMark: 100
                  }
                ]}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="category" />
                  <PolarRadiusAxis domain={[0, 100]} />
                  <Radar name="Progress Score" dataKey="score" stroke="#2563eb" fill="#2563eb" fillOpacity={0.3} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Symptom Improvement</span>
                  <span className="font-bold text-green-600">{localProgressData.progress_scores.symptom_improvement.toFixed(0)}%</span>
                </div>
                <Progress value={localProgressData.progress_scores.symptom_improvement} className="h-3" />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Biomarker Improvement</span>
                  <span className="font-bold text-teal-600">{localProgressData.progress_scores.biomarker_improvement.toFixed(0)}%</span>
                </div>
                <Progress value={localProgressData.progress_scores.biomarker_improvement} className="h-3" />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Lifestyle Improvement</span>
                  <span className="font-bold text-purple-600">{localProgressData.progress_scores.lifestyle_improvement.toFixed(0)}%</span>
                </div>
                <Progress value={localProgressData.progress_scores.lifestyle_improvement} className="h-3" />
              </div>

              <div className="pt-4 border-t">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-lg">Overall Progress</span>
                  <span className="font-bold text-2xl text-green-600">{localProgressData.progress_scores.overall_improvement.toFixed(0)}%</span>
                </div>
                <Progress value={localProgressData.progress_scores.overall_improvement} className="h-4" />
              </div>
            </div>
          </div>
        </Card>

        {/* Milestones & Timeline */}
        <div className="grid md:grid-cols-2 gap-8">
          <Card className="p-6">
            <h3 className="text-xl font-semibold mb-4">Achieved Milestones</h3>
            <div className="space-y-4">
              {localProgressData.achieved_milestones.map((milestone, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <Award className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-800">{milestone.name}</p>
                    <p className="text-sm text-green-600">{milestone.description}</p>
                    <p className="text-xs text-green-500">
                      Achieved {format(parseISO(milestone.achieved_date), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {localProgressData.upcoming_milestones.length > 0 && (
              <div className="mt-6">
                <h4 className="font-semibold mb-3">Upcoming Milestones</h4>
                {localProgressData.upcoming_milestones.map((milestone, index) => (
                  <div key={index} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="font-semibold text-blue-800">{milestone.name}</p>
                    <p className="text-sm text-teal-600">{milestone.description}</p>
                    <div className="mt-2">
                      <Progress value={milestone.completion_percentage} className="h-2" />
                      <p className="text-xs text-blue-500 mt-1">
                        {milestone.completion_percentage.toFixed(0)}% complete
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-6">
            <h3 className="text-xl font-semibold mb-4">Progress Timeline</h3>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {localProgressData.timeline_events.map((event, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className={`w-3 h-3 rounded-full mt-2 ${
                    event.type === 'start' ? 'bg-blue-500' :
                    event.type === 'milestone' ? 'bg-green-500' : 'bg-purple-500'
                  }`}></div>
                  <div>
                    <p className="font-semibold">{event.title}</p>
                    <p className="text-sm text-muted-foreground">{event.description}</p>
                    <p className="text-xs text-gray-400">
                      {format(parseISO(event.date), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Current Status Summary */}
        <Card className="p-6 bg-gradient-to-r from-blue-50 to-green-50">
          <h3 className="text-xl font-semibold mb-4">Current Status Summary</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-semibold mb-2">Key Improvements</h4>
              <ul className="text-sm space-y-1">
                <li>• Energy levels increased by 112% (4 → 8.5)</li>
                <li>• Sleep quality improved by 150% (3 → 7.5)</li>
                <li>• 15 lbs weight loss achieved</li>
                <li>• HbA1c normalized (6.1% → 5.4%)</li>
                <li>• Inflammation reduced 68% (3.8 → 1.2 mg/L)</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-2">Next Focus Areas</h4>
              <ul className="text-sm space-y-1">
                <li>• Continue weight optimization</li>
                <li>• Maintain current energy levels</li>
                <li>• Further CRP reduction</li>
                <li>• HbA1c stabilization</li>
                <li>• Long-term maintenance planning</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-2">Protocol Effectiveness</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Overall Success Rate:</span>
                  <span className="font-bold text-green-600">78%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Treatment Adherence:</span>
                  <span className="font-bold text-teal-600">94%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Side Effects:</span>
                  <span className="font-bold text-green-600">Minimal</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Patient Satisfaction:</span>
                  <span className="font-bold text-green-600">Excellent</span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Protocol Feedback & Questions Component */}
        <ProtocolFeedback 
          protocol={generatedProtocol}
          protocolId={assessmentId}
        />
      </div>
    );
  };

  const Navigation = () => {
    // Primary nav items shown on desktop
    const primaryNavItems = [
      { id: 'home', label: 'Home', icon: null },
      { id: 'assessment', label: 'Assessment', icon: null },
      { id: 'chat', label: 'Dr. Peptide', icon: MessageCircle },
      { id: 'dashboard', label: 'Dashboard', icon: Activity },
      { id: 'calculator', label: 'Calculator', icon: Calculator },
      { id: 'community', label: 'Community', icon: Users },
      { id: 'protocols', label: 'Protocols', icon: BookOpen },
      { id: 'peptides', label: 'Peptides', icon: FlaskConical },
      { id: 'profile', label: 'Profile', icon: User },
      ...(currentUser?.role === 'admin' ? [{ id: 'admin', label: 'Admin', icon: Settings }] : [])
    ];

    // All nav items for mobile menu
    const allNavItems = [
      { id: 'home', label: 'Home', icon: null },
      { id: 'assessment', label: 'Assessment', icon: null },
      { id: 'chat', label: 'Dr. Peptide', icon: MessageCircle },
      { id: 'dashboard', label: 'Dashboard', icon: Activity },
      { id: 'calculator', label: 'Dosage Calculator', icon: Calculator },
      { id: 'progress', label: 'Progress Tracking', icon: TrendingUp },
      { id: 'protocols', label: 'Protocols', icon: BookOpen },
      { id: 'peptides', label: 'Peptides', icon: FlaskConical },
      { id: 'network', label: 'Network', icon: Network },
      { id: 'community', label: 'Community', icon: Users },
      { id: 'feedback', label: 'Feedback', icon: MessageCircle },
      ...(currentUser?.role === 'admin' ? [{ id: 'admin', label: 'Intelligence', icon: Brain }] : []),
      { id: 'profile', label: currentUser?.full_name?.split(' ')[0] || 'Profile', icon: User }
    ];

    const handleNavClick = (viewId) => {
      setCurrentView(viewId);
      setIsMobileMenuOpen(false);
    };

    // Open a notification: deep-link to its post in the Community view and mark
    // (already marked read by the bell/page before this fires).
    const handleOpenNotification = (n) => {
      if (n?.post_id) {
        setCommunityPostId(n.post_id);
        setCurrentView('community');
      } else {
        setCurrentView('notifications');
      }
      setIsMobileMenuOpen(false);
    };

    // E3: Pre-Release Access Mode
    const isPreRelease = process.env.REACT_APP_PRE_RELEASE_MODE === 'true';

    return (
      <>
        <nav className="bg-white border-b border-teal-100 sticky top-0 z-50" data-testid="main-nav">
          <div className="max-w-6xl mx-auto px-4 lg:px-6">
            <div className="flex justify-between items-center h-14">
              {/* Logo */}
              <div className="flex items-center gap-2 cursor-pointer flex-shrink-0" onClick={() => handleNavClick('home')} data-testid="nav-logo">
                <img src="/images/dr_peptide_closeup.jpeg" alt="Dr. Peptide by PeptideProtocols.ai" className="h-8 w-8 rounded-lg" />
                <span className="text-lg lg:text-xl font-bold bg-gradient-to-r from-teal-700 to-teal-500 bg-clip-text text-transparent">
                  PeptideProtocols.ai
                </span>
                {isPreRelease && (
                  <span className="hidden sm:inline-block px-2 py-0.5 bg-teal-100 text-teal-700 text-[10px] font-semibold rounded-full uppercase tracking-wider" data-testid="early-access-badge">
                    Early Access
                  </span>
                )}
              </div>
              
              {/* Desktop Navigation - visible at md (768px+) */}
              <div className="hidden md:flex items-center gap-0.5">
                {primaryNavItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item.id)}
                    data-testid={`nav-${item.id}`}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                      currentView === item.id
                        ? 'bg-teal-600 text-white'
                        : 'text-gray-600 hover:text-teal-700 hover:bg-teal-50'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
                {/* Notification bell — to the right of the user/profile nav,
                    logged-in only. Polls unread count; dropdown deep-links. */}
                {isAuthenticated() && (
                  <NotificationBell
                    onOpenNotification={handleOpenNotification}
                    onSeeAll={() => handleNavClick('notifications')}
                  />
                )}
              </div>

              {/* Mobile cluster: bell + hamburger - visible below md (768px) */}
              <div className="md:hidden flex items-center gap-1">
                {isAuthenticated() && (
                  <NotificationBell
                    onOpenNotification={handleOpenNotification}
                    onSeeAll={() => handleNavClick('notifications')}
                  />
                )}
                <Button
                  variant="ghost"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-2 flex items-center justify-center"
                  style={{ height: '44px', width: '44px' }}
                  aria-label={`${isMobileMenuOpen ? 'Close' : 'Open'} navigation menu`}
                  aria-expanded={isMobileMenuOpen}
                  data-testid="hamburger-menu-btn"
                >
                  {isMobileMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Mobile/Tablet Menu Overlay */}
          {isMobileMenuOpen && (
            <div 
              className="md:hidden absolute left-0 right-0 top-14 bg-white border-b border-gray-200 shadow-lg z-40"
              data-testid="mobile-menu"
            >
              <div className="px-4 py-2 space-y-1 max-h-[70vh] overflow-y-auto">
                {allNavItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item.id)}
                    data-testid={`mobile-nav-${item.id}`}
                    className={`w-full flex items-center gap-2 px-3 py-3 h-11 rounded-md text-sm font-medium transition-colors ${
                      currentView === item.id 
                        ? 'bg-teal-600 text-white' 
                        : 'text-gray-700 hover:bg-teal-50'
                    }`}
                  >
                    {item.icon && <item.icon className="h-4 w-4" />}
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Backdrop for mobile menu */}
          {isMobileMenuOpen && (
            <div 
              className="md:hidden fixed inset-0 bg-black bg-opacity-25 z-30 top-14"
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}
        </nav>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      {/* Welcome Onboarding Modal */}
      {showWelcome && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 overflow-y-auto pb-20 max-h-[100dvh]" data-testid="welcome-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600 to-cyan-600 p-6 text-center">
              <img src="/images/dr_peptide_friendly_wave.png" alt="Dr. Peptide - AI clinical agent welcome" className="h-24 w-24 mx-auto rounded-full border-4 border-white shadow-lg mb-3" />
              <h2 className="text-2xl font-bold text-white">Welcome to PeptideProtocols.ai!</h2>
              <p className="text-teal-100 mt-1 text-sm">Your AI-powered peptide therapy companion</p>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-600 text-sm text-center">Here's how to get started:</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-teal-50 rounded-lg">
                  <div className="bg-teal-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</div>
                  <div><div className="font-medium text-sm text-gray-800">Run a Patient Assessment</div><p className="text-xs text-gray-500">Fill out the 6-step wizard with patient data for a personalized protocol</p></div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-teal-50 rounded-lg">
                  <div className="bg-teal-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</div>
                  <div><div className="font-medium text-sm text-gray-800">Chat with Dr. Peptide</div><p className="text-xs text-gray-500">Ask questions about peptides, dosing, interactions, and protocols</p></div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-teal-50 rounded-lg">
                  <div className="bg-teal-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</div>
                  <div><div className="font-medium text-sm text-gray-800">Upload Labs & Genetics</div><p className="text-xs text-gray-500">Get genetically-informed protocols by uploading lab reports or genetic data</p></div>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button 
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={() => { setShowWelcome(false); localStorage.removeItem('pp_show_welcome'); setCurrentView('assessment'); }}
                  data-testid="welcome-start-assessment"
                >
                  Start Assessment
                </Button>
                <Button 
                  variant="outline" className="flex-1"
                  onClick={() => { setShowWelcome(false); localStorage.removeItem('pp_show_welcome'); }}
                  data-testid="welcome-explore"
                >
                  Explore Platform
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Patient Summary Modal */}
      {showPatientSummary && patientSummary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b bg-gradient-to-r from-green-50 to-blue-50">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                👤 Patient-Friendly Summary
              </h2>
              <p className="text-gray-600 mt-2">
                Simplified explanation ready to print and share with your patient
              </p>
              <div className="text-xs text-gray-500 mt-1">
                Generated: {patientSummary.timestamp}
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 bg-white">
              <div className="prose max-w-none">
                <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-800 font-medium">
                        Patient-Friendly Format
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        This summary uses simple language suitable for patient handouts
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                  {patientSummary.summary}
                </div>

                {/* Dr. Peptide sign-off. Kept here and in
                    ProtocolView.jsx's patient-summary modal. Removal
                    is blocked by .github/workflows/quality-guard.yml. */}
                <div className="mt-8 flex flex-col items-center gap-2 print:break-inside-avoid">
                  <img
                    src="/images/dr_peptide_thumbs_up.png"
                    alt="Dr. Peptide"
                    style={{ width: 96, height: 96, objectFit: "contain" }}
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                  <p className="text-sm font-semibold" style={{ color: "#20808D" }}>
                    You've got this.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex gap-3 flex-wrap">
              <Button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(patientSummary.summary);
                    alert("✅ Patient summary copied! Ready to paste into documents.");
                  } catch (err) {
                    console.error("Copy error:", err);
                  }
                }}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                📋 Copy Summary
              </Button>
              <Button
                onClick={() => {
                  const printWindow = window.open('', '', 'height=800,width=800');
                  printWindow.document.write('<html><head><title>Patient Summary</title>');
                  printWindow.document.write('<style>body{font-family: Arial, sans-serif; padding: 40px; line-height: 1.6;} h1{color: #2563eb;} h2{color: #059669; margin-top: 20px;} ul{margin-left: 20px;}</style>');
                  printWindow.document.write('</head><body>');
                  printWindow.document.write('<h1>Your Health Summary</h1>');
                  printWindow.document.write('<p style="color: #666; font-size: 12px;">Generated: ' + patientSummary.timestamp + '</p>');
                  printWindow.document.write('<hr style="margin: 20px 0;">');
                  printWindow.document.write('<div>' + patientSummary.summary.replace(/\n/g, '<br>') + '</div>');
                  printWindow.document.write('<hr style="margin: 40px 0 20px 0;">');
                  printWindow.document.write('<p style="font-size: 11px; color: #666;">This information is provided for educational purposes. Always follow your healthcare provider\'s specific instructions.</p>');
                  printWindow.document.write('</body></html>');
                  printWindow.document.close();
                  printWindow.print();
                }}
                variant="outline"
                className="border-green-300 text-green-700"
              >
                🖨️ Print Summary
              </Button>
              <Button
                onClick={() => {
                  setShowPatientSummary(false);
                }}
                variant="outline"
              >
                Close
              </Button>
              <div className="flex-1"></div>
              <Button
                onClick={() => {
                  setShowPatientSummary(false);
                  setPatientSummary(null);
                }}
                variant="outline"
                className="text-gray-500"
              >
                Discard
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Extraction Preview Modal */}
      {showExtractionModal && extractionPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                {extractionPreview.fallback ? '📎 Files Attached' : (
                  extractionPreview.fileType === 'genetics' 
                    ? '🧬 Genetic Analysis Complete!' 
                    : '✅ Lab Analysis Complete!'
                )}
              </h2>
              <p className="text-gray-600 mt-2">
                {extractionPreview.fallback 
                  ? 'Files attached successfully. Please review and add values manually below.' 
                  : extractionPreview.fileType === 'genetics'
                    ? `Extracted genetic markers from ${extractionPreview.files.length} file(s). This will be used for preventive peptide protocol recommendations.`
                    : `Extracted data from ${extractionPreview.files.length} file(s). Review and confirm before adding to Patient Information.`}
              </p>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {extractionPreview.files.map((file, idx) => (
                <div key={idx} className={`mb-6 rounded-lg p-4 border-2 ${
                  file.fileType === 'genetics' || extractionPreview.fileType === 'genetics'
                    ? 'bg-purple-50 border-purple-200'
                    : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">
                      {file.fileType === 'genetics' || extractionPreview.fileType === 'genetics' ? '🧬' : '📄'}
                    </span>
                    <div>
                      <div className="font-semibold text-lg flex items-center gap-2">
                        {file.filename}
                        {(file.fileType === 'genetics' || extractionPreview.fileType === 'genetics') && (
                          <span className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded">
                            GENETIC TEST
                          </span>
                        )}
                      </div>
                      {file.filesize && (
                        <div className="text-sm text-gray-500">
                          {(file.filesize / 1024).toFixed(1)} KB
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-white rounded p-3 border border-gray-300">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      {file.fileType === 'genetics' || extractionPreview.fileType === 'genetics' 
                        ? '🧬 Genetic Markers Identified:' 
                        : '🔬 Extracted Lab Data:'}
                    </div>
                    <pre className="text-sm whitespace-pre-wrap font-mono">
                      {file.analysis?.extracted_text || file.analysis?.interpretation || JSON.stringify(file.analysis, null, 2)}
                    </pre>
                  </div>
                  
                  {(file.fileType === 'genetics' || extractionPreview.fileType === 'genetics') && (
                    <div className="mt-3 p-3 bg-purple-100 border border-purple-300 rounded text-sm">
                      <div className="font-semibold text-purple-900 mb-1">💡 Preventive Protocol Generation:</div>
                      <div className="text-purple-800">
                        Based on identified genetic markers, Dr. Peptide will recommend preventive peptides for:
                        • Risk variant mitigation
                        • Metabolic optimization
                        • Longevity enhancement
                        • Disease prevention
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex gap-3">
              <Button
                onClick={applyExtractedData}
                className="flex-1"
              >
                ✓ Add to Patient Information
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowExtractionModal(false);
                  setExtractionPreview(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Template Quick Entry Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold">📋 Quick Entry Templates</h2>
              <p className="text-gray-600 mt-2">Select a template to quickly enter common lab panels</p>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 space-y-3">
              <button
                onClick={() => {
                  const template = `\n\nMETABOLIC PANEL:
- Glucose: ___ mg/dL
- BUN: ___ mg/dL
- Creatinine: ___ mg/dL
- Sodium: ___ mEq/L
- Potassium: ___ mEq/L
- Chloride: ___ mEq/L
- CO2: ___ mEq/L
- Calcium: ___ mg/dL`;
                  setPatientContext(prev => (prev || '') + template);
                  setShowTemplateModal(false);
                }}
                className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="font-semibold text-lg">Metabolic Panel (BMP)</div>
                <div className="text-sm text-gray-600">8 values: Glucose, BUN, Creatinine, Electrolytes</div>
              </button>
              
              <button
                onClick={() => {
                  const template = `\n\nLIPID PANEL:
- Total Cholesterol: ___ mg/dL
- LDL Cholesterol: ___ mg/dL
- HDL Cholesterol: ___ mg/dL
- Triglycerides: ___ mg/dL
- VLDL: ___ mg/dL`;
                  setPatientContext(prev => (prev || '') + template);
                  setShowTemplateModal(false);
                }}
                className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="font-semibold text-lg">Lipid Panel</div>
                <div className="text-sm text-gray-600">5 values: Total, LDL, HDL, Triglycerides, VLDL</div>
              </button>
              
              <button
                onClick={() => {
                  const template = `\n\nTHYROID PANEL:
- TSH: ___ mIU/L
- Free T4: ___ ng/dL
- Free T3: ___ pg/mL
- TPO Antibodies: ___ IU/mL`;
                  setPatientContext(prev => (prev || '') + template);
                  setShowTemplateModal(false);
                }}
                className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="font-semibold text-lg">Thyroid Panel</div>
                <div className="text-sm text-gray-600">4 values: TSH, Free T4, Free T3, Antibodies</div>
              </button>
              
              <button
                onClick={() => {
                  const template = `\n\nHbA1c & GLUCOSE:
- Fasting Glucose: ___ mg/dL
- HbA1c: ___% 
- Insulin (fasting): ___ uIU/mL`;
                  setPatientContext(prev => (prev || '') + template);
                  setShowTemplateModal(false);
                }}
                className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="font-semibold text-lg">Diabetes Screening</div>
                <div className="text-sm text-gray-600">3 values: Fasting Glucose, HbA1c, Insulin</div>
              </button>
            </div>
            
            <div className="p-6 border-t bg-gray-50">
              <Button
                variant="outline"
                onClick={() => setShowTemplateModal(false)}
                className="w-full"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {currentView === 'home' && <HomePage />}
      {currentView === 'terms' && (
        <div className="max-w-3xl mx-auto p-6 md:p-10 space-y-6" data-testid="terms-page">
          <h1 className="text-3xl font-bold text-slate-800">Terms of Service</h1>
          <p className="text-xs text-slate-400">Last updated: March 2026</p>
          <div className="prose prose-slate prose-sm max-w-none space-y-4 text-sm text-slate-600 leading-relaxed">
            <h2 className="text-lg font-semibold text-slate-700">1. Acceptance of Terms</h2>
            <p>By accessing PeptideProtocols.ai ("the Platform"), operated by Dallas Regenerative LLC, you agree to be bound by these Terms of Service. If you do not agree, do not use the Platform.</p>
            <h2 className="text-lg font-semibold text-slate-700">2. Platform Purpose</h2>
            <p>PeptideProtocols.ai is a clinical decision support tool designed for licensed healthcare practitioners. The Platform provides AI-generated peptide therapy protocol suggestions based on patient data inputs. All output is intended as decision support only and does not constitute medical advice, diagnosis, or treatment.</p>
            <h2 className="text-lg font-semibold text-slate-700">3. User Responsibilities</h2>
            <p>Users must be licensed healthcare practitioners. All clinical decisions remain the sole responsibility of the treating practitioner. Users must independently verify all AI-generated recommendations before clinical application.</p>
            <h2 className="text-lg font-semibold text-slate-700">4. Limitation of Liability</h2>
            <p>Dallas Regenerative LLC provides the Platform "as is" without warranties of any kind. We are not liable for clinical outcomes, treatment decisions, or any damages arising from use of the Platform.</p>
            <h2 className="text-lg font-semibold text-slate-700">5. Data Handling</h2>
            <p>Patient data entered into the Platform is used solely for generating protocol recommendations. See our Privacy Policy for details on data storage and handling.</p>
            <h2 className="text-lg font-semibold text-slate-700">6. Intellectual Property</h2>
            <p>All content, algorithms, and proprietary formulas on the Platform are the property of Dallas Regenerative LLC.</p>
            <h2 className="text-lg font-semibold text-slate-700">7. Contact</h2>
            <p>Questions about these terms: info@peptideprotocols.ai</p>
          </div>
          <Button variant="outline" onClick={() => setCurrentView('home')} className="mt-4">Back to Home</Button>
        </div>
      )}
      {currentView === 'privacy' && (
        <div className="max-w-3xl mx-auto p-6 md:p-10 space-y-6" data-testid="privacy-page">
          <h1 className="text-3xl font-bold text-slate-800">Privacy Policy</h1>
          <p className="text-xs text-slate-400">Last updated: March 2026</p>
          <div className="prose prose-slate prose-sm max-w-none space-y-4 text-sm text-slate-600 leading-relaxed">
            <h2 className="text-lg font-semibold text-slate-700">1. Information We Collect</h2>
            <p>We collect information you provide directly: practitioner registration details, patient assessment data (demographics, labs, genetics), and feedback submissions.</p>
            <h2 className="text-lg font-semibold text-slate-700">2. How We Use Information</h2>
            <p>Patient data is used to generate personalized peptide protocol recommendations. Anonymized outcome data may be used to improve our AI models. We do not sell personal data to third parties.</p>
            <h2 className="text-lg font-semibold text-slate-700">3. Data Storage & Security</h2>
            <p>Data is stored on encrypted servers. Access is restricted to authorized personnel. We implement industry-standard security measures to protect your data.</p>
            <h2 className="text-lg font-semibold text-slate-700">4. Data Retention</h2>
            <p>Patient assessment data is retained for the duration of your account. You may request deletion of your data at any time by contacting us.</p>
            <h2 className="text-lg font-semibold text-slate-700">5. Third-Party Services</h2>
            <p>We use AI language model services (OpenAI, Anthropic) for protocol generation. Patient data sent to these services is processed according to their respective privacy policies and data processing agreements.</p>
            <h2 className="text-lg font-semibold text-slate-700">6. Your Rights</h2>
            <p>You have the right to access, correct, or delete your personal data. Contact info@peptideprotocols.ai for data requests.</p>
            <h2 className="text-lg font-semibold text-slate-700">7. Contact</h2>
            <p>Privacy inquiries: info@peptideprotocols.ai | Dallas Regenerative LLC</p>
          </div>
          <Button variant="outline" onClick={() => setCurrentView('home')} className="mt-4">Back to Home</Button>
        </div>
      )}
      {currentView === 'about-dr-peptide' && (
        <div className="max-w-4xl mx-auto p-6 md:p-10" data-testid="about-dr-peptide-page">
          <div className="text-center mb-8">
            <img src="/images/dr_peptide_standalone.jpeg" alt="Dr. Peptide - AI clinical agent for peptide therapy protocols" className="w-48 md:w-64 mx-auto rounded-2xl shadow-lg border border-teal-200 mb-6" />
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800">Meet Dr. Peptide — The World's First Clinical AI Agent</h1>
            <p className="text-teal-600 mt-2 text-lg font-medium">AI-Powered Peptide Protocol Intelligence for Practitioners</p>
          </div>
          <div className="prose prose-slate max-w-none space-y-6 text-sm md:text-base text-slate-600 leading-relaxed">
            <p>
              <strong>Dr. Peptide</strong> (also known as <strong>Doctor Peptide</strong>) is the world's first clinical AI agent purpose-built for peptide therapy. Developed by <strong>Dallas Regenerative LLC</strong>, Dr. Peptide was created to solve a critical gap in regenerative medicine: the lack of personalized, evidence-based peptide protocols tailored to each patient's unique biology.
            </p>
            <h2 className="text-xl font-semibold text-slate-700">How Dr. Peptide Works</h2>
            <p>
              Dr. Peptide uses the collective intelligence of contributing practitioners and medical advisors, combined with <strong>Dual-LLM and LRM processing</strong> and <strong>17 specialized AI agents</strong>, working in concert to analyze your patient's lab values, genetic markers, health history, and lifestyle data. The result is a comprehensive peptide protocol that includes specific dosing, administration routes, stacking combinations, monitoring schedules, and real PubMed citations.
            </p>
            <p>
              Unlike generic peptide dosing guides, Dr. Peptide generates truly personalized protocols. If your patient has elevated CRP and low Vitamin D, the protocol will account for those specific findings. If they're on metformin or thyroid medication, Dr. Peptide screens for drug interactions automatically.
            </p>
            <h2 className="text-xl font-semibold text-slate-700">Built by Practitioners, for Practitioners</h2>
            <p>
              Led by Chief Medical Advisor Venkat Sethuraman, MD, Dr. Peptide is informed by the clinical experience of all contributing peptide therapy practitioners. Every protocol recommendation reflects real-world clinical insights combined with peer-reviewed research from over <strong>2,800 studies</strong>.
            </p>
            <h2 className="text-xl font-semibold text-slate-700">Why Doctor Peptide Exists</h2>
            <p>
              Peptide therapy is one of the fastest-growing fields in functional and regenerative medicine. Yet practitioners often rely on fragmented resources — forum posts, manufacturer pamphlets, and anecdotal protocols. Dr. Peptide consolidates this knowledge into a single clinical decision support platform, giving you evidence-based peptide protocols backed by real citations.
            </p>
            <h2 className="text-xl font-semibold text-slate-700">Key Capabilities</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>AI-powered assessment wizard analyzing labs, genetics, and health data</li>
              <li>Real PubMed citations (PMIDs) for every recommended peptide</li>
              <li>Drug interaction screening and safety monitoring</li>
              <li>Dosage calculator with reconstitution and weight-based dosing</li>
              <li>Patient dashboard with progress tracking</li>
              <li>Library of 87+ pre-built peptide protocols and 64+ peptide profiles</li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button onClick={() => setCurrentView('chat')} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="about-chat-cta">
                <MessageCircle className="h-4 w-4 mr-2" /> Chat with Dr. Peptide
              </Button>
              <Button onClick={() => setCurrentView('home')} variant="outline" className="border-teal-300 text-teal-700">
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      )}
      {currentView === 'peptide-protocols-landing' && (
        <div className="max-w-4xl mx-auto p-6 md:p-10" data-testid="peptide-protocols-landing-page">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800">Peptide Protocols — Evidence-Based, AI-Generated for Your Patients</h1>
            <p className="text-teal-600 mt-2 text-base md:text-lg font-medium">Personalized peptide therapy protocols powered by Dr. Peptide's clinical AI</p>
          </div>
          <div className="prose prose-slate max-w-none space-y-6 text-sm md:text-base text-slate-600 leading-relaxed">
            <p>
              <strong>Peptide protocols</strong> are structured treatment plans that specify which peptides to use, at what doses, via which routes, and in what combinations for a specific patient's health goals. Unlike one-size-fits-all dosing guides, Dr. Peptide generates protocols that are <strong>personalized to each patient's biology</strong> — their labs, genetics, medical history, and lifestyle.
            </p>
            <h2 className="text-xl font-semibold text-slate-700">Why Personalization Matters</h2>
            <p>
              A 45-year-old male athlete with elevated inflammatory markers needs a fundamentally different peptide protocol than a 60-year-old female with Hashimoto's and chronic fatigue. Generic peptide dosing guides can't account for these differences. Dr. Peptide can.
            </p>
            <h2 className="text-xl font-semibold text-slate-700">Example Protocol Summaries</h2>
            <div className="grid gap-4 not-prose">
              {[
                { name: 'BPC-157', indication: 'Tissue Repair & GI Healing', dose: '250-500 mcg subcutaneous twice daily', mechanism: 'VEGF/FGF angiogenesis, nitric oxide synthesis, gastric mucosal protection', studies: 'Sikiric P et al., 2018 (PMID: 29737246)' },
                { name: 'TB-500 (Thymosin Beta-4)', indication: 'Wound Healing & Recovery', dose: '2-5 mg subcutaneous twice weekly', mechanism: 'G-actin sequestration, cell migration, NF-kB anti-inflammatory', studies: 'Crockford D et al., 2010 (PMID: 20955329)' },
                { name: 'GHK-Cu', indication: 'Skin Regeneration & Anti-Aging', dose: '200 mcg subcutaneous 2-3x weekly', mechanism: 'Collagen synthesis, SOD/glutathione upregulation, tissue remodeling', studies: 'Pickart L et al., 2015 (PMID: 25883940)' },
                { name: 'Thymosin Alpha-1', indication: 'Immune Modulation', dose: '1.6 mg subcutaneous twice weekly', mechanism: 'TLR signaling, Th1/Th2 balance, NK cell activation', studies: 'Tuthill C et al., 2010 (PMID: 20955325)' },
              ].map((p, i) => (
                <Card key={i} className="p-4 border-teal-100">
                  <h3 className="font-semibold text-teal-800 text-base">{p.name}</h3>
                  <p className="text-xs text-teal-600 font-medium">{p.indication}</p>
                  <div className="mt-2 text-sm text-slate-600 space-y-1">
                    <p><span className="font-medium">Standard Dose:</span> {p.dose}</p>
                    <p><span className="font-medium">Mechanism:</span> {p.mechanism}</p>
                    <p><span className="font-medium">Key Study:</span> {p.studies}</p>
                  </div>
                </Card>
              ))}
            </div>
            <div className="text-center pt-6">
              <p className="text-slate-500 mb-4 text-sm">Ready to generate your first personalized peptide protocol?</p>
              <Button onClick={() => setCurrentView('assessment')} className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3" data-testid="protocols-landing-cta">
                <Zap className="h-4 w-4 mr-2" /> Start Assessment
              </Button>
            </div>
          </div>
        </div>
      )}
      {currentView === 'assessment' && (_isBetaOnly ? <SubscriptionRequired setCurrentView={setCurrentView} /> : <AssessmentWizardComponent assessment={assessment} setAssessment={setAssessment} assessmentId={assessmentId} setAssessmentId={setAssessmentId} setGeneratedProtocol={setGeneratedProtocol} setCurrentView={setCurrentView} />)}
      {currentView === 'chat' && <DrPeptideChatComponent setCurrentView={setCurrentView} />}
      {currentView === 'progress' && (_isBetaOnly ? <SubscriptionRequired setCurrentView={setCurrentView} /> : <ProgressTracking patientId={selectedPatientId || assessment.patient_name} />)}
      {currentView === 'protocols' && <ProtocolLibrary />}
      {currentView === 'peptides' && <PeptidesView />}
      {currentView === 'calculator' && <DosageCalculator peptidesDatabase={peptidesDatabase} protocolLibrary={protocolLibrary} />}
      {currentView === 'admin' && currentUser?.role === 'admin' && <KnowledgeAdmin onBack={() => setCurrentView('home')} />}
      {currentView === 'profile' && <ProfilePage currentUser={currentUser} onUserUpdate={setCurrentUser} setCurrentView={setCurrentView} />}
      {currentView === 'billing' && <BillingPage currentUser={currentUser} onUserUpdate={setCurrentUser} />}
      {currentView === 'admin' && currentUser?.role === 'admin' && <AdminDashboard />}
      {currentView === 'admin' && (!currentUser || currentUser?.role !== 'admin') && (
        <div className="max-w-md mx-auto mt-20 text-center p-8 bg-white rounded-lg shadow-lg border-2 border-red-300" data-testid="admin-access-denied">
          <div className="text-red-500 text-5xl mb-4">&#9888;</div>
          <h2 className="text-2xl font-bold text-red-600 mb-3">Access Denied</h2>
          <p className="text-gray-600 mb-6">You don't have permission to view this page. This section is restricted to administrators.</p>
          <Button onClick={() => setCurrentView('home')} className="bg-teal-600 hover:bg-teal-700 text-white px-6">Return to Home</Button>
        </div>
      )}
      {currentView === 'network' && <CollectiveIntelligenceNetwork />}
      {currentView === 'community' && <CommunityForum onBack={() => setCurrentView('home')} currentUser={currentUser} initialPostId={communityPostId} />}
      {currentView === 'notifications' && <NotificationsPage onOpenNotification={(n) => { if (n?.post_id) { setCommunityPostId(n.post_id); setCurrentView('community'); } }} />}
      {currentView === 'feedback' && <FeedbackSystem />}
      {currentView === 'dashboard' && (_isBetaOnly ? <SubscriptionRequired setCurrentView={setCurrentView} /> : <PatientDashboard setCurrentView={setCurrentView} assessment={assessment} setAssessment={setAssessment} setAssessmentId={setAssessmentId} generatedProtocol={generatedProtocol} currentUser={currentUser} setSelectedPatientId={setSelectedPatientId} />)}
      {currentView === 'protocol' && <ProtocolViewComponent generatedProtocol={generatedProtocol} assessment={assessment} setCurrentView={(v) => (v === 'assessment' ? startNewAssessment() : setCurrentView(v))} currentUser={currentUser} />}

      {/* Site-Wide Footer — hidden on mobile when chat is active */}
      <footer className={`border-t border-slate-200 bg-slate-50 mt-auto ${currentView === 'chat' ? 'hidden md:block' : ''}`} data-testid="site-footer">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-slate-600">
            <div>
              <div className="font-semibold text-slate-800 mb-2">PeptideProtocols.ai</div>
              <p className="text-xs leading-relaxed">AI-powered clinical decision support for peptide therapy practitioners. Evidence-based protocols personalized to each patient's biology.</p>
            </div>
            <div>
              <div className="font-semibold text-slate-800 mb-2">Legal</div>
              <div className="space-y-1">
                <button onClick={() => setCurrentView('terms')} className="block text-teal-600 hover:text-teal-800 text-xs" data-testid="footer-tos-link">Terms of Service</button>
                <button onClick={() => setCurrentView('privacy')} className="block text-teal-600 hover:text-teal-800 text-xs" data-testid="footer-privacy-link">Privacy Policy</button>
              </div>
            </div>
            <div>
              <div className="font-semibold text-slate-800 mb-2">Resources</div>
              <div className="space-y-1">
                <a href="/about-dr-peptide" className="block text-teal-600 hover:text-teal-800 text-xs no-underline" data-testid="footer-about-link">About Dr. Peptide</a>
                <a href="/peptide-protocols" className="block text-teal-600 hover:text-teal-800 text-xs no-underline" data-testid="footer-protocols-link">Peptide Protocols</a>
                <a href="/providers" className="block text-teal-600 hover:text-teal-800 text-xs no-underline" data-testid="footer-providers-link">Find a Provider</a>
              </div>
            </div>
            <div>
              <div className="font-semibold text-slate-800 mb-2">Contact</div>
              <p className="text-xs">info@peptideprotocols.ai</p>
              <p className="text-xs mt-1">Dallas Regenerative LLC</p>
            </div>
          </div>
          <div className="border-t border-slate-200 mt-6 pt-4 text-center text-xs text-slate-400 space-y-2">
            <p data-testid="medical-disclaimer">PeptideProtocols.ai is a clinical decision support tool. All protocols must be reviewed by the prescribing practitioner before implementation. This is not medical advice.</p>
            <p>© June 4, 2025 Dallas Regenerative LLC</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Wrapper to handle navigation from public pages into auth
function PublicHomeWrapper({ setCurrentUser }) {
  const navigate = useNavigate();
  
  // If already authenticated, redirect to /app
  useEffect(() => {
    const stored = localStorage.getItem('pp_auth');
    if (stored) {
      try {
        const data = JSON.parse(stored);
        if (data.token && new Date() < new Date(data.expiry)) {
          navigate('/app');
        }
      } catch {}
    }
  }, [navigate]);
  
  return (
    <PublicHomePage 
      onSignIn={() => navigate('/login')} 
      onSignUp={() => navigate('/signup')} 
    />
  );
}

function UnderReviewScreen({ currentUser }) {
  return (
    <div data-testid="under-review-screen" className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Clock className="w-8 h-8 text-amber-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-3">Application Under Review</h1>
        <p className="text-slate-600 mb-6 leading-relaxed">
          Thank you for registering, {currentUser?.full_name?.split(' ')[0] || 'Doctor'}. Your practitioner account is being reviewed by our team. You'll receive an email once approved.
        </p>
        <div className="bg-slate-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-slate-500">Registered as</p>
          <p className="text-sm font-medium text-slate-800">{currentUser?.email}</p>
        </div>
        <button
          data-testid="under-review-logout-btn"
          onClick={() => { localStorage.removeItem('pp_auth'); window.location.href = '/'; }}
          className="text-sm text-teal-600 hover:text-teal-800 font-medium"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function SubscriptionRequired({ setCurrentView }) {
  return (
    <div data-testid="subscription-required-screen" className="max-w-md mx-auto mt-20 text-center">
      <Card className="border-slate-200 shadow-lg">
        <CardHeader>
          <div className="w-14 h-14 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <Lock className="w-7 h-7 text-teal-600" />
          </div>
          <CardTitle className="text-xl text-slate-900">Subscription Required</CardTitle>
          <CardDescription className="text-slate-600 mt-2">
            This feature is available with a full practitioner subscription. You currently have beta chat-only access.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Button onClick={() => setCurrentView('chat')} className="bg-teal-600 hover:bg-teal-700 text-white">
            <MessageCircle className="w-4 h-4 mr-2" /> Back to Dr. Peptide Chat
          </Button>
          <a href="/signup" className="text-sm text-teal-600 hover:text-teal-800 font-medium">
            Learn About Full Access
          </a>
        </CardContent>
      </Card>
    </div>
  );
}

function AuthenticatedApp({ currentUser, setCurrentUser, initialView }) {
  // Approval gate: pending users see Under Review screen
  // Beta access bypass — 1111 users skip approval gate
  let isBetaAccess = false;
  try { const b = JSON.parse(localStorage.getItem("beta_access") || "{}"); isBetaAccess = b.granted && b.code === "1111"; } catch {}
  if (currentUser && currentUser.role !== 'admin' && currentUser.application_status !== 'approved' && !isBetaAccess) {
    return <UnderReviewScreen currentUser={currentUser} />;
  }
  return (
    <AuthGate onUserChange={setCurrentUser}>
      <PeptideProtocolsApp currentUser={currentUser} setCurrentUser={setCurrentUser} initialView={initialView} />
    </AuthGate>
  );
}

function LoginRoute({ setCurrentUser }) {
  return (
    <AuthGate onUserChange={setCurrentUser} initialView="login">
      <RedirectToApp />
    </AuthGate>
  );
}

function SignupRoute({ setCurrentUser }) {
  return (
    <AuthGate onUserChange={setCurrentUser} initialView="signup">
      <RedirectToApp />
    </AuthGate>
  );
}

// 2026-06-23 audit fix: /forgot-password and /reset-password were defined in
// AuthGate's isAuthRoute allowlist but had no matching <Route>, so the catch-all
// silently bounced bookmarked password-reset URLs to the landing page. Both
// routes now render the same AuthGate in forgot-password view.
function ForgotPasswordRoute({ setCurrentUser }) {
  return (
    <AuthGate onUserChange={setCurrentUser} initialView="forgot">
      <RedirectToApp />
    </AuthGate>
  );
}

function RedirectToApp() {
  const navigate = useNavigate();
  useEffect(() => { navigate('/app'); }, [navigate]);
  return null;
}

function App() {
  const APP_VERSION = '5.0.0';
  const BUILD_TIME = process.env.REACT_APP_BUILD_TIME || 'dev';
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    document.body.setAttribute('data-app-version', APP_VERSION);
  }, []);
  
  return (
    <div className="App">
      <Toaster position="top-center" richColors closeButton />
      <BrowserRouter>
        <Routes>
          {/* Public routes - no auth required, visible to Google */}
          <Route path="/" element={<PublicHomeWrapper setCurrentUser={setCurrentUser} />} />
          <Route path="/about-dr-peptide" element={<AboutDrPeptidePage />} />
          <Route path="/peptide-protocols" element={<PeptideProtocolsPage />} />
          <Route path="/providers" element={<ProviderDirectory />} />
          <Route path="/protocols/:slug" element={<ProtocolSeoPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/ambassador-terms" element={<AmbassadorTermsPage />} />
          <Route path="/login" element={<LoginRoute setCurrentUser={setCurrentUser} />} />
          <Route path="/signup" element={<SignupRoute setCurrentUser={setCurrentUser} />} />
          <Route path="/forgot-password" element={<ForgotPasswordRoute setCurrentUser={setCurrentUser} />} />
          <Route path="/reset-password" element={<ForgotPasswordRoute setCurrentUser={setCurrentUser} />} />
          
          {/* Authenticated app - all practitioner features */}
          <Route path="/app" element={<AuthenticatedApp currentUser={currentUser} setCurrentUser={setCurrentUser} />} />
          <Route path="/billing" element={<AuthenticatedApp currentUser={currentUser} setCurrentUser={setCurrentUser} initialView="billing" />} />
          <Route path="/progress" element={<AuthenticatedApp currentUser={currentUser} setCurrentUser={setCurrentUser} initialView="progress" />} />
          
          {/* Catch-all: redirect to public home */}
          <Route path="*" element={<PublicHomeWrapper setCurrentUser={setCurrentUser} />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;